<template>
    <div class="prescription-component" :style="overflowStyle">
        <component :is="card ? 'card' : 'div'">
            <!-- RX SEARCH FILTER-->
            <div v-if="showSearchBar" class="mb-2">
                <SearchComponent v-model="prescription" :search-u-r-l="`/Prescription`" @input="fetchPrescription" />
                <b-button variant="info" @click="newPrescription" :disabled="patient?.deceased">New Rx</b-button>
                <hr />
            </div>

            <EntityAlertsComponent ref="entity-alerts" :patient="patient" :prescription="prescription"
                :prescriber="doctor" :drug="drug" :drug-source="drug?.source" :store-id="prescription?.storeID"
                :fill-date="prescription?.fillDate" :rx-status="statusCode" :expiration-date="expiration"
                :additional-alerts="additionalAlerts" class="pb-3">
                <template v-if="!isBatchMode" #default>
                    <h1 class="m-0">Prescription Details</h1>
                </template>
                <template v-if="prescription.rxNumber" #accessory>
                    <div class="d-flex mr-4" style="gap: 8px;">
                        <b-link v-b-modal.prescription-contacts-modal>Contacts <i class="fas fa-phone" /></b-link>
                        <history-modal v-model="prescription" />
                    </div>
                </template>
            </EntityAlertsComponent>

            <DispensingErrorAlertComponent v-for="(err, index) in dispenseErrors" :key="err.id"
                v-model="dispenseErrors[index]" :buttonTitles="dispenseErrorAlertButtonTitles"
                @onStatusChange="fetchDispenseError" />

            <!-- MedSync Banner -->
            <MedSyncBanner
                v-if="patient && patient.id"
                ref="medsync-banner"
                :patient-id="patient.id"
                :rx-id="prescription.rxID"
                @view-details="handleViewMedSyncDetails"
                @add-to-medsync="handleAddToMedSync" />

            <b-row v-show="!!(prescription && prescription.rxNumber)">
                <b-col>
                    <b-alert v-if="patient && patient.deceased" :show="true" variant="danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        <strong> Patient is deceased.</strong>
                        Refills cannot be created for deceased patients.
                    </b-alert>
                    <b-alert v-if="newRefillMode" v-model="notAllowNewRefill" variant="warning">
                        This prescription <b>is not refillable</b>.
                        <span v-if="prescription && !prescription.hasBeenShipped">
                            Previous fill has not been shipped yet.
                        </span>
                    </b-alert>
                    <span v-if="newRefillMode">
                        Save changes to create new refill <strong>Rx #</strong>{{ nextRefillRxId }}
                    </span>
                    <span v-else> <strong>Rx #</strong>{{ prescriptionIdentifier }} </span>

                    <div v-if="isBatchMode &&
                        prescriptionsInBatch &&
                        prescriptionsInBatch.length > 1
                    ">
                        <b>In Batch: </b>
                        <span>({{ batchIndex + 1 }} / {{ prescriptionsInBatch.length }})</span>
                    </div>
                    <router-link v-if="patient?.id" :to="{ name: 'PatientDashboard', params: { id: patient.id } }"
                        class="ml-2 pull-right">
                        <span>Go To Profile</span> <i class="fas fa-user" />
                    </router-link>
                    <router-link
                        v-if="patient?.id && $permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create Order')"
                        :to="{ name: 'Order', params: { patientId: patient.id } }" class="ml-2 pull-right">
                        <span>New Order</span> <i class="fas fa-plus-circle" />
                    </router-link>
                    <router-link
                        :to="{ name: 'Tasks', params: { taskId: 0 }, query: { patientId: prescription.patientID, prescriptionId: prescription.rxID } }"
                        class="pull-right ml-2">
                        New Task <i class="fas fa-check-circle" />
                    </router-link>
                    <a v-if="$permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create PAP')" href="#"
                        class="pull-right" @click.prevent="createPAPApplication">
                        Create PAP Application <i class="fas fa-file" />
                    </a>
                </b-col>
            </b-row>

            <b-row v-show="!!(prescription && prescription.rxNumber)">
                <b-col>
                    <b-button v-if="!prescription.imageID && !prescription.escriptID" v-b-modal="'add-image'"
                        class="text-left" variant="danger">Add Image
                    </b-button>
                    <b-button v-if="prescription.imageID && !prescription.escriptID" v-b-modal="'add-image'"
                        class="text-left mr-2" variant="danger">Replace Image
                    </b-button>
                    <b-button
                        v-if="hasHardcopyPermission && prescription.imageID && !prescription.escriptID"
                        variant="warning"
                        @click="openPdfAnnotator">
                        <i class="fas fa-edit"></i> Annotate PDF
                    </b-button>
                </b-col>
                <b-col class="text-right">
                    <!-- GENERAL RX OPTIONS -->
                    <span v-show="prescription.isActive" class="text-right" style="display: inline-block">
                        <EscriptButtonComponent v-model="prescription" :escript="escript" />
                    </span>
                    <b-button v-if="allowNewRefill && !refillBatchMode" :disabled="newRefillMode || patient?.deceased" variant="info"
                        @click="newRefill">
                        New Refill
                    </b-button>

                    <span
                        v-show="(prescription && (prescription.status || prescription.status === 0) && prescription.status != 4)">
                        <b-button v-if="!!(
                            prescription &&
                            prescription.rxNumber &&
                            prescription.rfNumber != null
                        )
                        " v-b-modal="'transfer-rx'" :disabled="disableInternalTransfer" variant="success">
                            Internal Transfer
                        </b-button>
                        <b-button v-b-modal="'external-transfer-modal'" variant="success">
                            External Transfer
                        </b-button>
                    </span>
                    <span>
                        <b-button v-show="showRefillAuthRequest" v-b-modal="'request-refill-auth-fax'" :disabled="!(
                            prescription &&
                            prescription.rxNumber &&
                            prescription.rfNumber != null
                        ) || patient?.deceased
                            " variant="info">
                            Refill Authorization
                        </b-button>
                        <b-dropdown text="Contact Prescriber" variant="info" right>
                            <b-dropdown-item v-b-modal="'request-rx-clarification-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )
                                ">
                                RX Clarification
                            </b-dropdown-item>
                            <b-dropdown-item v-b-modal="'request-alternate-rx-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )
                                ">
                                Request Alternate Medication
                            </b-dropdown-item>
                            <b-dropdown-divider v-if="filteredCustomContactPrescriptionTemplates.length" />
                            <b-dropdown-item v-for="template in filteredCustomContactPrescriptionTemplates"
                                :key="template.id"
                                @click="customContactPrescriberTemplate = template; $bvModal.show('custom-prescriber-fax')"
                                :disabled="!(
                                    prescription &&
                                    prescription.rxNumber &&
                                    prescription.rfNumber != null
                                )
                                    ">
                                {{ template.name }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </span>
                    <span v-show="prescription &&
                        prescription.rxNumber &&
                        prescription.pV2ID &&
                        !newRefillMode
                        ">
                        <b-button variant="danger" @click="cancelPV2"> Cancel PV2 </b-button>
                    </span>
                    <span v-show="prescription && prescription.rxNumber && prescription.isVoid">
                        <b-button variant="danger" @click="unVoid"> Unvoid </b-button>
                    </span>
                </b-col>
            </b-row>

            <b-row>
                <!-- PRESCRIPTION IMAGE -->
                <b-col id="prescription-image" class="ml-1" cols="12" md="5">
                    <div class="image-wrapper">
                        <div class="actions">
                            <b-button v-if="queueItemID" variant="success" @click="showDocuStore = true" class="m-0">
                                Save as Patient Document
                            </b-button>
                            <b-button v-if="queueItemID" variant="danger" @click="markAsNotAScript" class="m-0">
                                Not A Script
                            </b-button>
                            <b-button v-show="showChopperButton" v-b-modal.pdf-choppa variant="info" class="m-0">
                                Split Document
                                <pdf-chopper-modal id="pdf-choppa" :image-id="prescription.imageID"
                                    @newQueueItems="updateQueueItems" v-model="pdfChopperVisible" />
                            </b-button>
                            <template v-if="prescription.escriptID">
                                <b-button v-if="prescription.rxNumber" @click="redrawEscript" class="m-0">Redraw
                                    Escript
                                </b-button>
                                <b-button variant="warning" v-b-modal.all-escripts class="m-0">
                                    All Escript Images
                                    <escript-list-modal v-model="prescription.rxID" id="all-escripts" />
                                </b-button>
                                <b-button
                                    v-if="hasHardcopyPermission"
                                    variant="warning"
                                    @click="openPdfAnnotator"
                                    class="m-0">
                                    <i class="fas fa-edit"></i> Annotate
                                </b-button>
                            </template>
                        </div>

                        <div>
                            <ImageComponent ref="prescriptionImage" :escriptID="prescription.escriptID"
                                :imageID="prescription.imageID" :rxNumber="prescription.rxNumber"
                                class="script-image" />
                        </div>

                        <!-- Typing/Verification Info -->
                        <div class="typing-info mt-2">
                            <div v-if="prescription.createdBy">
                                <b>Typed By</b>
                                <span class="name">{{ prescription.createdBy?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.created) }}</span>
                            </div>
                            <div v-if="prescription.pV2ID">
                                <b>Verified By</b>
                                <span class="name">{{ prescription.pv2By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv2) }}</span>
                            </div>
                        </div>
                    </div>
                </b-col>
                <b-col>
                    <!-- PRESCRIPTION FORM -->
                    <template v-if="!showPatientForm &&
                        !showDoctorForm &&
                        !editPatientForm &&
                        !editDoctorForm &&
                        !showDocuStore &&
                        !showPharmacyForm
                    ">
                        <!-- Entities Selection, Drug and Patient Information -->
                        <b-row>
                            <!-- Entities Selection -->
                            <b-col>
                                <SearchComponent :id="store && +store.id" v-model="store"
                                    :disabled="shouldLockEscriptProcessingStore" label="Store" prepopulate
                                    search-u-r-l="/Store" :params="searchStoreGetParams"
                                    :autoselect="newMode || fromIngestion ? autoselectStore : undefined" required />
                            </b-col>
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <b-row>
                                            <b-col>
                                                <strong>Store Location:</strong>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                {{ storeLocationInformation }}
                                            </b-col>
                                        </b-row>
                                    </b-col>
                                    <b-col>
                                        <b-row>
                                            <b-col>
                                                <strong>Store Phone:</strong>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                {{ store && store.phoneNumber | formatPhone }}
                                            </b-col>
                                        </b-row>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <strong>NPI:</strong>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        {{ store && store.npiNumber }}
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                        <hr />
                        <b-row>
                            <b-col>
                                <SearchComponent ref="patient-search-component" :id="patient && +patient.id"
                                    v-model="patient" create-new-object createNewLabel="Create New Patient"
                                    :disabled="disableFields || disablePatient" label="Patient" search-u-r-l="/Patient"
                                    required @createNewObject="showPatientForm = true" @input="patientUpdate" />
                                <b-button size="sm" variant="outline-primary" @click="startEditPatient">
                                    Edit Patient
                                </b-button>
                                <b-button :disabled="!(patient && patient.id)" size="sm" variant="outline-primary"
                                    @click="openRxHistory">
                                    Show RX History
                                </b-button>
                            </b-col>
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <strong>Patient Phone:</strong>
                                        <br />
                                        {{ patient && patient.phoneDisplay }}
                                        <a v-if="patient?.phoneNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                            <i class="fas fa-copy" @click.prevent="copyText(patient.phoneTwilio)" />
                                        </a>

                                    </b-col>
                                    <b-col>
                                        <strong>DOB:</strong>
                                        <br />
                                        {{ patientDOB }}
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                        <hr />
                        <b-row>
                            <b-col>
                                <SearchComponent ref="prescriber-search-component" :id="doctor && +doctor.id"
                                    v-model="doctor" create-new-object createNewLabel="Create New Doctor"
                                    :disabled="disableFields" label="Doctor" search-u-r-l="/Prescriber" required
                                    @createNewObject="showDoctorForm = true" @input="doctorUpdate" />
                                <b-button size="sm" variant="outline-primary" @click="editDoctorForm = true">
                                    Edit Doctor
                                </b-button>
                                <template v-if="doctor?.contacts?.length">
                                    <b-button id="office-contacts-popover" size="sm" variant="outline-primary">
                                        View Office Contacts
                                    </b-button>
                                    <b-popover target="office-contacts-popover" triggers="click blur"
                                        custom-class="office-contacts-popover">
                                        <b-card v-for="c in doctor.contacts" :key="c.id" style="box-shadow: none;">
                                            <strong>{{ c.firstName }} {{ c.lastName }}</strong>
                                            <div>
                                                {{ c.title }}
                                            </div>
                                            <div>
                                                {{ c.phoneNumber | formatPhone }}
                                                <a href="#" class="mx-1" v-b-tooltip.hover title="Copy"><i class="fas fa-copy"
                                                        @click.prevent="copy(c.phoneNumber)" /></a>
                                            </div>
                                            <div>
                                                <small class="text-muted">
                                                    {{ c.note }}
                                                </small>
                                            </div>
                                        </b-card>
                                    </b-popover>
                                </template>
                            </b-col>
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <strong>Prescriber Phone:</strong>
                                        <br />
                                        {{ doctor?.phoneNumber | formatPhone }}
                                        <a v-if="doctor?.phoneNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                            <i class="fas fa-copy" @click.prevent="copy(doctor.phoneNumber)" />
                                        </a>
                                        <br />
                                        <strong>Prescriber Fax:</strong>
                                        <br />
                                        {{ doctor?.faxNumber | formatPhone }}
                                        <a v-if="doctor?.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                            <i class="fas fa-copy" @click.prevent="copy(doctor.faxNumber)" />
                                        </a>
                                        <br />
                                        <strong>Prescriber Address:</strong>
                                        <br />
                                        <template v-if="doctor?.addressCity">
                                            <div v-if="doctor.address1">{{ doctor.address1 }}</div>
                                            <div v-if=doctor.address2>{{ doctor.address2 }}</div>
                                            <div>
                                                {{
                                                    [doctor.addressCity?.trim(), doctor.addressState?.trim()].join(", ")
                                                }}
                                                {{ doctor.addressZip }}
                                            </div>
                                        </template>
                                    </b-col>
                                    <b-col>
                                        <div v-if="doctor?.altNumber">
                                            <strong>Alt Phone:</strong>
                                            <br />
                                            {{ doctor?.altNumber | formatPhone }}
                                            <a v-if="doctor?.altNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                                <i class="fas fa-copy" @click.prevent="copy(doctor.altNumber)" />
                                            </a>
                                        </div>
                                        <div v-if="doctor?.altFaxNumber">
                                            <strong>Alt Fax:</strong>
                                            <br />
                                            {{ doctor?.altFaxNumber | formatPhone }}
                                            <a v-if="doctor?.altFaxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                                <i class="fas fa-copy" @click.prevent="copy(doctor.altFaxNumber)" />
                                            </a>
                                        </div>
                                        <div :class="{ 'text-danger': isNPIInvalid }">
                                            <strong>NPI:</strong>
                                            <br />
                                            {{ doctor?.npiNumber }}
                                        </div>
                                        <div v-if="doctorProgram.id">
                                            <strong>Prescriber Program:</strong><br />
                                            {{ doctorProgram.name }}
                                        </div>
                                        <div>
                                            <strong>DEA:</strong>
                                            <br />
                                            {{ doctor?.deaNumber }}
                                        </div>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                        <hr />
                        <b-row v-show="showDrugChange">
                            <b-col>
                                <strong class="text-danger">NDC Change from DrFirst SmartSig. Verification
                                    required.</strong>
                            </b-col>
                        </b-row>
                        <b-row id="drug-row">
                            <b-col id="drug-search-col">
                                <SearchPrescriptionDetailComponent :id="drug && drug.drugId"
                                    :disabled="disableFields" v-model="drug" label="Drug"
                                    :paramsInGetById="drugSearchParams" :equivalent-drugs="equivalentDrugs"
                                    search-u-r-l="/Drug" required>
                                </SearchPrescriptionDetailComponent>
                                <EquivalentDrugsComponent
                                    v-show="drug && drug.drugId && !disableFields && drug.source == 0"
                                    :closeEquivalentDrugList="true" :drug="drugAsDrug"
                                    @drugsSelected="equivalentDrugsSearched" />
                                <br>
                                <SearchComponent :id="program && +program.id" ref="program-search-component"
                                    v-model="program" clearable :disabled="disableProgramChange" label="Program"
                                    :params="searchProgramGetParams" :paramsInGetById="{ includeFlags: true }"
                                    prepopulate search-u-r-l="/Program" required @input="programUpdate" />
                            </b-col>
                            <b-col id="drug-details-col">
                                <!-- Drug Details-->
                                <b-row v-if="!!drugDetails">
                                    <!-- Drug information-->
                                    <b-col cols="12">
                                        <b-row>
                                            <b-col>
                                                <strong>NDC:</strong>
                                                {{ drugNdc }}
                                                <br />
                                                <strong>Package Size:</strong>
                                                {{ drugPackageSize }}
                                                <template v-if="drugDetails.program">
                                                    <br />
                                                    <strong>Drug Program:</strong>
                                                    {{ drugDetails.program.name }}
                                                </template>
                                            </b-col>
                                            <b-col>
                                                <strong>Drug Status:</strong>
                                                {{ drugStatus }}
                                                <template v-if="drugLastFilled">
                                                    <br />
                                                    <strong>Last Filled:</strong>
                                                    {{ drugLastFilled }}
                                                </template>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                <strong v-show="!drugInventory" class="text-warning">
                                                    Unknown Stock
                                                </strong>
                                                <strong v-show="drugInStock" class="text-success">
                                                    In Stock
                                                </strong>
                                                <strong v-show="drugOutOfStock" class="text-danger">
                                                    Out Of Stock
                                                </strong>
                                            </b-col>
                                        </b-row>
                                        <b-row v-if="drugDetails && drugDetails.packageDescription">
                                            <b-col>
                                                <strong>Package Description:</strong><br>
                                                {{ drugDetails.packageDescription }}
                                            </b-col>
                                        </b-row>
                                    </b-col>

                                    <!-- Drug Images--->
                                    <b-col cols="12" v-show="!imageError">
                                        <div v-if="imageURLs.length">
                                            <strong>Product Image:</strong>
                                            <br />
                                            <div class="text-center">
                                                <b-img v-for="image of imageURLs" :key="image" :src="image" class="mb-2"
                                                    style="max-height: 150px" thumbnail fluid @load="imageError = false"
                                                    @error="imageError = true" />
                                            </div>
                                        </div>
                                        <i v-else-if="!!drugDetails">No images found for this selected product.</i>
                                    </b-col>
                                </b-row>
                                <b-row v-else-if="loadingDrugDetails">
                                    <b-col>
                                        <b-spinner class="mr-2" label="Spinning" />
                                        <strong>Loading product details...</strong>
                                    </b-col>
                                </b-row>
                                <b-row v-else-if="errorLoadingDrugDetails && !loadingDrugDetails">
                                    <b-col>
                                        <strong style="color: red">Error Loading product details.</strong>
                                        <span>Try searching the drug again.</span>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>

                        <hr />

                        <!-- Quantities -->
                        <b-row>
                            <b-col>
                                <fg-input v-model.number="prescription.quantity" :class="validationClasses.quantity"
                                    :disabled="disableQuantity" :no-wheel="true" label="Quantity" min="0" required
                                    type="number" @input="changeDispense" />
                            </b-col>
                            <b-col>
                                <fg-input v-model.number="dispensedQuantity" :class="validationClasses.dispense"
                                    :disabled="disableFields" label="Dispensed" min="0" required type="number"
                                    @input="changeDispense" />
                            </b-col>
                            <b-col>
                                <fg-input v-if="!prescription || !prescription.rfNumber"
                                    v-model.number="prescription.refills" :class="validationClasses.refills"
                                    :disabled="disableRefills" label="Refills" min="0" required type="number" />
                                <fg-input v-else v-model.number="fillsLeft" :disabled="true" label="Refills" min="0"
                                    required type="number" />
                            </b-col>
                            <b-col>
                                <fg-input v-model.number="prescription.daySupply" :class="validationClasses.daySupply"
                                    :disabled="disableFields" label="Days' Supply" min="0" required type="number" />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <DirectionsComponent ref="directions-component" v-model="prescription.directions"
                                    :disabled="disableBillingComponent"
                                    :program-id="prescription.programID"
                                    :dispensed-amount="prescription.dispensed"
                                    @days-supply-calculated="handleDaysSupplyCalculated" />
                            </b-col>
                        </b-row>
                        <b-overlay :show="loadingDirections" rounded="sm">
                            <card v-if="directionsTranslations?.paraphrases?.length" plain>
                                <b-form-group label="Directions to be translated:" label-class="font-weight-bold">
                                    <b-form-checkbox v-for="phrase in directionsTranslations?.paraphrases"
                                        :key="phrase.id" :value="phrase.id" :checked="selectedTranslation"
                                        @input="selectedTranslation = $event">
                                        {{ phrase.token }}
                                    </b-form-checkbox>
                                </b-form-group>
                                <br>
                                <b-button variant="info" size="sm" @click="translateDirections">
                                    Translate
                                </b-button>
                            </card>
                        </b-overlay>

                        <b-alert :show="sigIsSmartSig" variant="danger">
                            <strong class="d-flex" style="gap: 7px;">
                                <i class="fa fa-warning"></i>
                                Sig interpreted from DrFirst SmartSig.
                                Verification required.
                            </strong>
                        </b-alert>

                        <b-row>
                            <b-col>
                                <fg-input label="Rx Memo">
                                    <textarea v-model="prescription.memo" class="memo form-control" />
                                </fg-input>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <EnumSelector ref="origin-selector" v-model="originCode" :disabled="disableFields"
                                    :enum-codes="originCodes" label="Origin" required />
                            </b-col>
                            <b-col>
                                <EnumSelector ref="daw-selector" v-model="dawCode" :disabled="disableFields"
                                    :enum-codes="dawCodes" label="DAW" required />
                            </b-col>
                        </b-row>
                        <b-row v-show="originCode == originCodes.Transfer">
                            <b-col>
                                <search-component v-model="originPharmacy" :disabled="disableFields"
                                    search-u-r-l="/StoredPharmacy" label="Origin Pharmacy" create-new-label="Create New"
                                    create-new-object clearable @createNewObject="showPharmacyForm = true"
                                    @input="prescription.originPharmacyId = originPharmacy?.id" />
                            </b-col>
                            <b-col></b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <fg-input label="Fill Date" required>
                                    <el-date-picker v-model="prescription.fillDate" :class="validationClasses.fillDate"
                                        :disabled="disableFields || rxWasBilled" format="MM/dd/yyyy" type="date">
                                    </el-date-picker>
                                </fg-input>
                            </b-col>
                            <b-col>
                                <fg-input :disabled="disableFields" label="Written" required>
                                    <el-date-picker v-model="writtenDate" :class="validationClasses.writtenDate"
                                        :disabled="disableFields" format="MM/dd/yyyy" type="date" />
                                </fg-input>
                            </b-col>
                            <b-col>
                                <fg-input label="Expires" required>
                                    <el-date-picker v-model="expiration" :class="validationClasses.expirationDate"
                                        :disabled="disableFields" format="MM/dd/yyyy" type="date" />
                                </fg-input>
                            </b-col>
                            <b-col v-if="showDispensingFields">
                                <fg-input v-model="prescription.partnerFillNumber" :disabled="disableBillingComponent"
                                    label="Partner Fill #" />
                            </b-col>
                        </b-row>
                        <b-row align-v="end" v-if="showDispensingFields">
                            <b-col>
                                <b-table :items="dispenseItems"
                                    :fields="['lotNumber', 'serialNumber', 'expirationDate', 'quantity']"
                                    class="dispense-items" show-empty empty-text="No Dispense Items on Prescription">
                                    <template #cell(lotNumber)="{ value, index }">
                                        <fg-input :value="value" :disabled="disableFields"
                                            @input="updateDispenseItem('lotNumber', index, $event)" class="m-0" />
                                    </template>
                                    <template #cell(serialNumber)="{ value, index }">
                                        <fg-input :value="value" :disabled="disableFields"
                                            @input="updateDispenseItem('serialNumber', index, $event)" class="m-0" />
                                    </template>
                                    <template #cell(expirationDate)="{ value, index }">
                                        <fg-input class="m-0">
                                            <el-date-picker :value="value" :disabled="disableFields" format="MM/dd/yyyy"
                                                type="date"
                                                @input="updateDispenseItem('expirationDate', index, $event)" />
                                        </fg-input>
                                    </template>
                                    <template #cell(quantity)="{ value, index }">
                                        <fg-input :value="value" :disabled="disableFields" type="number"
                                            @input="updateDispenseItem('quantity', index, $event)" class="m-0" />
                                    </template>
                                </b-table>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-row>
                                    <b-col cols="6">
                                        <EnumSelector v-model="statusCode" :disabled="disableFields"
                                            :elementsToDiscard="notSelectableStatus" :enum-codes="statusCodes"
                                            label="Status" required />
                                    </b-col>
                                    <b-col cols="6">
                                        <EnumSelector v-model="deliveryCode" :clearable="true" :disabled="disableFields"
                                            :enum-codes="deliveryCodes" label="Delivery" required />
                                    </b-col>
                                    <b-col
                                        v-if="statusCode == statusCodes.OnHold || statusCode == statusCodes.Discontinued"
                                        cols="6">
                                        Substatus*
                                        <v-select
                                            :value="{ value: substatusCode, label: applicableSubStatuses.find(ss => ss.value == substatusCode)?.label }"
                                            :options="applicableSubStatuses.filter(ss => ss.active)"
                                            :disabled="disableFields" @input="substatusCode = $event?.value"
                                            :clearable="false" />
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                        <b-row v-if="prescription.transferMyRxId?.length && transferMyRxStatus != 'canceled'">
                            <b-col cols="auto">
                                {{ transferMyRxStatus }}
                            </b-col>
                            <b-col cols="auto"
                                v-if="transferMyRxStatus != 'accepted' && transferMyRxStatus != 'rejected' && transferMyRxStatus != 'canceled'">
                                <b-button v-b-modal.cancel-reason-modal>Cancel Transfer</b-button>
                            </b-col>
                        </b-row>

                    </template>

                    <PatientFormComponent v-if="editPatientForm" :button-titles="patientButtonTitles"
                        :escript-response-patient="patient" create-new-patient hide-insurance
                        @close="editPatientForm = false" @created="onPatientCreated" />

                    <PatientFormComponent v-if="showPatientForm" :button-titles="patientButtonTitles"
                        :create-new-patient="true" :escript-response-patient="escriptPatient" hide-insurance
                        @close="showPrescriptionForm('hidePatientForm')" @created="onPatientCreated" />

                    <DoctorFormComponent v-if="editDoctorForm" :button-titles="doctorButtonTitles"
                        :escript-response-prescriber="doctor" create-new-doctor @close="editDoctorForm = false"
                        @created="onDoctorCreated" />

                    <DoctorFormComponent v-if="showDoctorForm" :button-titles="doctorButtonTitles"
                        :create-new-doctor="true" :escript-response-prescriber="escriptPrescriber"
                        @close="showPrescriptionForm('hideDoctorForm')" @created="onDoctorCreated" />

                    <pharmacy-detail-page v-if="showPharmacyForm"
                        :button-titles="{ save: 'Save Changes', cancel: 'Close', new: '', clear: '' }" new-mode
                        @close="onPharmacyFormClose" @created="onPharmacyCreate" />

                    <PatientDocumentsForm v-if="showDocuStore" ref="patientDocumentsForm" :queueItem="queueItem"
                        @close="showPrescriptionForm()" @on-save="handlePatientDocSaved" />
                </b-col>
            </b-row>

            <hr />
            <b-alert v-model="showDrugAlert" dismissible variant="danger">
                <b>Drug Memo:</b> {{ drugAlert }}
            </b-alert>
            <b-alert v-model="showAlertIsOutOfState" variant="warning">
                <b>Prescription cannot be filled at this store.</b> Please transfer to appropriate
                store licensed in patient's state.
            </b-alert>
            <b-alert v-model="showInactiveDrugAlert" variant="danger">
                <b>Selected drug is not active</b>. Please select an active drug.
            </b-alert>
            <b-alert v-model="showAgeAlert" variant="warning">
                <b>Prescription Fill Date too old, Manager Intervention Required</b>
            </b-alert>
            <!-- Custom Questions-->
            <b-row v-for="cat in drugCategories" :key="cat.id">
                <b-col>
                    <QuestionaryComponent :ref="`questionary_${cat.id}`" :categoryID="+cat.id"
                        :disable="!prescription.isActive" :prescription="prescription"
                        @saved-answers="questionarySaved">
                    </QuestionaryComponent>
                </b-col>
            </b-row>

            <h4>Insurance Billing</h4>
            <b-row>
                <b-col>
                    <InsuranceBillingComponent ref="insuranceComponent" :billOnlyMultilfills="isMultiFill"
                        :defaultPriceSchedule="program && program.priceSchedule" :disabled="disableBillingComponent"
                        :drugLicenseType="drugLicenseType" :newMode="newMode || newRefillMode" :patient="patient"
                        :prescription="prescription" :isMedSyncShortFill="medSyncShortFillData?.isMedSyncShortFill || false"
                        @clearRx="clearForm" @onClaimsLoaded="claimsLoaded"
                        @startSurescriptsElectronicPriorAuthorization="startSurescriptsElectronicPriorAuthorization
                        " @startSurescriptsElectronicPriorAuthorizationExpedited="startSurescriptsElectronicPriorAuthorizationExpedited
                        " @save-with-plan="saveAndBill" @rx-needs-refresh="fetchPrescription(prescription)"
                        @rx-transfer-refresh="rxTransferRefresh" @pricing-updated="updatePricing" />
                </b-col>
            </b-row>
            <hr />
            <!-- Hard Copy Note -->
            <b-row v-if="validFormForBilling && hasHardcopyPermission && (prescription.imageID || prescription.escriptID)">
                <b-col>
                    <fg-input label="Hardcopy Note">
                        <textarea v-model="addNote" class="memo form-control" placeholder="Enter note text to add to the prescription image..." />
                    </fg-input>
                    <b-button :disabled="!validFormForBilling ||
                        (prescription && !prescription.isActive) ||
                        !hasHardcopyPermission ||
                        !addNote.trim()" variant="success" @click="openQuickAnnotate">
                        Add Hardcopy Note
                    </b-button>
                </b-col>
            </b-row>
            <hr v-if="validFormForBilling && hasHardcopyPermission" />
            <!-- Patient Notes -->
            <b-row v-if="patient && patient.id">
                <b-col>
                    <NotesForm v-if="patient" :entityId="+patient.id" :entityType="patientEntityType"
                        :saveButtonLabel="'Add Patient Note'" :textBoxLabel="'Patient Note'"
                        @saved="refreshPatientNotes" />
                    <NotesList ref="patientNotesList" :entityId="+patient.id" :entityType="patientEntityType"
                        :pageSize="5" />
                </b-col>
            </b-row>

            <hr v-if="prescription" />

            <b-overlay :show="saving">
                <NewClearSaveButtons :titles="buttonTitles" :disable-new="disableNew" :disable-clear="disableClear"
                    :disable-save="disableSave" :disable-cancel="saving" show-clear-modal @cancel-clicked="cancel"
                    @clear-clicked="undoChanges" @new-clicked="saveAndContinue" @save-clicked="save" />
            </b-overlay>
        </component>

        <DebugViewComponent header="">
            <b-row>
                <b-col>
                    disableFieldsReason: {{ disableFieldsReason }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    disableBillingReason: {{ disableBillingReason }}
                </b-col>
            </b-row>
        </DebugViewComponent>
        <!-- Error resolve confirmation -->
        <b-modal id="modal_save_confirmation" hide-footer title="Was the error resolved?">
            <checkbox v-model="wasResolved">
                Confirming that the reported error was resolved
            </checkbox>
            <br />
            <NotesForm :bodyRequired="false" :entityId="dispenseError && +dispenseError.id"
                :entityType="dispenseErrorEntityType"
                :placeholder="'Introduce a note with additional information (not required)'"
                :prefixNote="`${wasResolved ? '** RESOLVED **' : ''}`" :saveButtonLabel="saveButtonLabel" :silent="true"
                @input="noteChanged" @saved="modalSaveAndResolve" />
        </b-modal>

        <b-modal id="add-image" title="Add Image" centered-title="xl">
            <b-row>
                <b-col>
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending" />
                </b-col>
            </b-row>
        </b-modal>

        <b-modal id="transfer-rx" hide-footer no-close-on-backdrop title="Transfer Rx to:">
            <b-row>
                <b-col>
                    <SearchComponent :id="newStore && +newStore.id" v-model="newStore" :label="`New Store`"
                        :prepopulate="true" :search-u-r-l="`/Store`" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button block class="mt-3" @click="$bvModal.hide('transfer-rx')">
                        Close
                    </b-button>
                </b-col>
                <b-col>
                    <b-button block class="mt-3" variant="success" @click="transferRx()">
                        Transfer
                    </b-button>
                </b-col>
            </b-row>
        </b-modal>

        <!-- External Transfer Modal -->
        <ExternalTransferModal
            :prescriptions="[prescription]"
            :store="store"
            :patient="patient"
            @transferred="onTransferCompleted" />

        <b-modal id="prescription-contacts-modal" title="Prescription Contacts" hide-footer>
            <b-table :items="prescriptionContacts" :fields="prescriptionContactFields" no-provider-sorting />
        </b-modal>

        <PatientRxHistoryModalComponent id="prescription-details-rx-history" ref="patient_rx_history_modal"
            :patient="patient" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :patient="patient" :prescriber="doctor" :prescription="prescription"
            :store="store" modal-id="request-refill-auth-fax" @saved="updateAlerts" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.RxClarification"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="request-rx-clarification-fax" @saved="updateAlerts" />

        <AlternateRxFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.AlternateRxRequest"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="request-alternate-rx-fax" @saved="updateAlerts" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :custom-template="customContactPrescriberTemplate"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="custom-prescriber-fax" @saved="updateAlerts" />

        <DiscontinueEquivalentScriptsModal
            v-model="showEquivalentModal"
            :patient-id="prescription.patientID"
            :package-id="prescription.packageID"
            :current-store-id="prescription.storeID"
            :current-rx-number="prescription.rxNumber"
            :initial-equivalent-prescriptions="equivalentPrescriptionsData"
            @discontinued="onEquivalentDiscontinued"
            @skipped="onEquivalentSkipped"
        />

        <patient-notes-modal-component v-if="prescription && prescription.patientID"
            :entity-id="prescription.patientID" />
        <doctor-notes-modal-component v-if="prescription && prescription.prescriberID"
            :entity-id="prescription.prescriberID" />
        <drug-notes-modal-component v-if="prescription && prescription.packageID" :entity-id="prescription.packageID"
            :drug-source="prescription.drugSource" />

        <b-modal id="cancel-reason-modal" title="Cancel Transfer" @ok="cancelTransferMyRxWithReason" @hidden="cancelReason = ''">
            <b-form-group label="Reason" label-for="reason-input" >
                <b-form-input id="reason-input" v-model="cancelReason"/>
            </b-form-group>
        </b-modal>

        <!-- MedSync Modals -->
        <MedSyncDetailsModal
            v-if="patient && patient.id"
            ref="medsync-details-modal"
            :patient-id="patient.id" />

        <AddToMedSyncModal
            v-if="patient && patient.id"
            ref="add-to-medsync-modal"
            :patient-id="patient.id"
            :rx-id="prescription.rxID"
            @added="handleMedSyncAdded" />

        <!-- PDF Annotation Modal -->
        <PdfAnnotationModal
            ref="pdf-annotation-modal"
            :image-id="annotationImageId"
            :escript-id="annotationEscriptId"
            :initial-text="quickAnnotateText"
            :auto-place="isQuickAnnotate"
            modal-id="prescription-pdf-annotation"
            title="Annotate Prescription Image"
            :require-permission="true"
            @saved="onPdfAnnotationSaved"
            @closed="onPdfAnnotationClosed" />

    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import Axios, { AxiosError, CancelTokenSource } from "axios";
import Vue2Dropzone from "vue2-dropzone";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import NewClearSaveButtons from "@/components/NewClearSaveButtons.vue";
import EnumSelector from "@/components/EnumSelector.vue";
import DispensingErrorAlertComponent from "@/components/DispensingError/DispensingErrorAlertComponent.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import NotesList from "@/components/Note/NotesList.vue";
import InsuranceBillingComponent from "@/components/InsurancePlan/InsuranceBillingComponent.vue";
import ImageComponent from "@/components/Prescription/ImageComponent.vue";
import EquivalentDrugsComponent from "@/components/Drug/EquivalentDrugsComponent.vue";
import QuestionaryComponent from "@/components/Questionary/QuestionaryComponent.vue";
import { Lock, LockHandler } from "@/mixins/LockHandler";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import PhoneFormatMixin from "@/mixins/PhoneFormatMixin";
import {
    DAWCode,
    OriginCode,
    Prescription,
    PrescriptionDispenseItem,
    RefillStatusCode,
    RxDeniedSubStatusId,
} from "@/models/Prescription";
import { DeliveryCode } from "@/models/DeliveryCode";
import { Prescriber } from "@/models/Prescriber";
import { Patient } from "@/models/Patient";
import { Program } from "@/models/Program";
import { DispenseError } from "@/models/DispenseError";
import { Store } from "@/models/Store";
import { QueueItem } from "@/models/QueueItem";
import { Drug, DrugStatus } from "@/models/Drug/Drug";
import { DrugCategory } from "@/models/DrugCategory";
import { Escript, EscriptResponse, EscriptResponseMessage } from "@/models/Escript";
import { FaxType as FaxToPhysicianFormType } from "@/models/PrescriberFollowUpCase";
import { Location } from "vue-router";
import { DatePicker } from "element-ui";
import { Note } from "@/models/Note";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import PatientRxHistoryModalComponent from "@/components/Patient/PatientRxHistoryModalComponent.vue";
import FaxToPhysicianFormModal from "@/components/Prescription/FaxToPhysicianFormModal.vue";
import AlternateRxFormModal from "@/components/Prescription/AlternateRxFormModal.vue";
import DiscontinueEquivalentScriptsModal from "@/components/Prescription/DiscontinueEquivalentScriptsModal.vue";
import type { EquivalentPrescription } from "@/models/EquivalentPrescription";
import DoctorFormComponent from "@/components/Doctor/DoctorFormComponent.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import PatientDocumentsForm from "@/components/Patient/PatientDocumentsForm.vue";
import DoctorFollowUpCasesAlertComponent from "@/components/Doctor/DoctorFollowUpCasesAlertComponent.vue";
import moment from "moment";
import { Claim } from "@/models/Claim";
import DirectionsComponent from "@/components/Prescription/DirectionsComponent.vue";
import EscriptButtonComponent from "@/components/Prescription/EscriptButtonComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { ValidatorGroup } from "@/util/ValidatorGroup";
import { AutoRefillSubscription } from "@/models/AutoRefillSubscription";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import { PharmacyRecord } from "@/models/Pharmacy";
import PharmacyDetailPage from "@/pages/Pharmacy/PharmacyDetailPage.vue";
import PharmacistDetailPage from "@/pages/Pharmacist/PharmacistDetailPage.vue";
import { PharmacyBenefitManager } from "@/models/PharmacyBenefitManager";
import EscriptListModal from "@/components/Prescription/EscriptListModal.vue";
import { BvTableCtxObject } from "bootstrap-vue";
import DebugViewComponent from "@/components/DebugViewComponent.vue";
import { OrderStatus } from "@/models/Order";
import { EntityType } from "@/models/EntityType";
import PdfChopperModal from "@/components/Prescription/PdfChopperModal.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import HelpPopup from "@/components/HelpPopup.vue";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import DrugInfoComponent from "@/components/Drug/DrugInfoComponent.vue";
import { PatientDocument } from "@/models/PatientDocument";
import EntityAlertsComponent, { Alert } from "@/components/EntityAlertsComponent.vue";
import RxTransferListModalComponent from "@/components/Patient/RxTransferListModalComponent.vue";
import SearchPrescriptionDetailComponent from "@/components/Prescription/SearchPrescriptionDetailComponent.vue";
import ExternalTransferModal from "@/components/Transfer/ExternalTransferModal.vue";
import MedSyncBanner from "@/components/MedSync/MedSyncBanner.vue";
import MedSyncDetailsModal from "@/components/MedSync/MedSyncDetailsModal.vue";
import AddToMedSyncModal from "@/components/MedSync/AddToMedSyncModal.vue";
import PdfAnnotationModal from "@/components/PdfAnnotationModal.vue";

let processingInsurance = false;
let previousRoute: any;

@Component({
    computed: {
        PharmacyBenefitManager() {
            return PharmacyBenefitManager;
        },
    },
    beforeRouteLeave(_to, _from, next) {
        if (processingInsurance) {
            alert("Please wait until insurance processing has finished");
            next(false);
        } else {
            next();
        }
    },
    beforeRouteEnter(_to: any, from: any, next: any) {
        previousRoute = from;
        next();
    },
    name: "PrescriptionDetailsPage",
    components: {
        SearchPrescriptionDetailComponent,
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        HistoryModal,
        PdfChopperModal,
        DebugViewComponent,
        EscriptListModal,
        PharmacistDetailPage,
        DirectionsComponent,
        NewClearSaveButtons,
        EnumSelector,
        InsuranceBillingComponent,
        ImageComponent,
        [DatePicker.name]: DatePicker,
        PatientFormComponent,
        DoctorFormComponent,
        DispensingErrorAlertComponent,
        NotesForm,
        NotesList,
        Checkbox,
        EquivalentDrugsComponent,
        EscriptButtonComponent,
        QuestionaryComponent,
        PatientRxHistoryModalComponent,
        PatientDocumentsForm,
        FaxToPhysicianFormModal,
        AlternateRxFormModal,
        DiscontinueEquivalentScriptsModal,
        DoctorFollowUpCasesAlertComponent,
        VueDropzone: Vue2Dropzone,
        SearchComponent,
        PharmacyDetailPage,
        HelpPopup,
        DrugInfoComponent,
        EntityAlertsComponent,
        ExternalTransferModal,
        MedSyncBanner,
        MedSyncDetailsModal,
        AddToMedSyncModal,
        PdfAnnotationModal,
    },
})
export default class PrescriptionDetailsPage extends Mixins(LockHandler, AutoModeHotkeyMixin, PhoneFormatMixin) {
    @Prop() prescriptionsInBatch!: Array<Prescription>;
    @Prop({ default: true, type: Boolean }) card!: Boolean;
    @Ref("program-search-component") programSearchComponent!: SearchComponent;
    @Ref("doctor-followup-alerts") doctorFollowupAlerts!: DoctorFollowUpCasesAlertComponent;
    @Ref('rxTransfersModalComponent') rxTransfersModalComponent!: RxTransferListModalComponent;
    @Ref('patient-search-component') patientSearchComponent!: SearchComponent;
    @Ref('prescriber-search-component') prescriberSearchComponent!: SearchComponent;
    @Ref('drug-search-component') drugSearchComponent!: SearchComponent;
    @Ref('directions-component') directionsComponent!: DirectionsComponent;
    @Ref('equivalent-drugs-component') equivalentDrugsComponent!: EquivalentDrugsComponent;
    @Ref('origin-selector') originComponent!: EnumSelector;
    @Ref('daw-selector') dawComponent!: EnumSelector;
    @Ref('insuranceComponent') insuranceComponent!: InsuranceBillingComponent;
    @Ref("entity-alerts") entityAlertsComponent!: EntityAlertsComponent;
    @Ref('medsync-banner') medSyncBanner!: MedSyncBanner;
    @Ref('medsync-details-modal') medSyncDetailsModal!: MedSyncDetailsModal;
    @Ref('add-to-medsync-modal') addToMedSyncModal!: AddToMedSyncModal;
    @Ref('pdf-annotation-modal') pdfAnnotationModal!: PdfAnnotationModal;
    @Ref('prescriptionImage') prescriptionImageComponent!: ImageComponent;

    errorLoadingDrugDetails = false;

    directionsTranslations: { paraphrases: { id: string, token: string }[], token: string } = {} as any;
    selectedTranslation: string = "";
    loadingDirections: boolean = false;

    // Enumerations
    protected originCodes = OriginCode;
    protected dawCodes = DAWCode;
    protected deliveryCodes = DeliveryCode;
    protected doctorButtonTitles = {
        new: "",
        clear: "",
        save: "Save Doctor",
        cancel: "Close Doctor Form",
    };
    protected imageURLs: string[] = [];
    protected imageError: boolean = false;
    protected faxFormTypeRequestEnum = FaxToPhysicianFormType;
    protected equivalentDrugs: Drug.EquivalentDrug[] = [];
    private cancelReason: string = "";

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    //Props
    @Prop({ default: false }) private refillBatchMode!: boolean;
    @Prop() private medSyncShortFillData?: {
        adjustedDaysSupply: number;
        adjustedDispensed: number;
        isMedSyncShortFill: boolean;
        medSyncAnchorDate: Date;
    };
    // Entities
    private prescription: Prescription = new Prescription();
    private store: Store = new Store();
    private newStore: Store = new Store();
    private patient: Patient = new Patient();
    private doctor: Prescriber = new Prescriber();
    private program: Program = new Program();
    private doctorProgram: Program = new Program();
    private drug: DrugProduct = new Drug();
    private escript: Escript = new Escript();
    private claims: Array<Claim> = [];
    private drugCategories: Array<DrugCategory> = [];
    private prescriptionLock: Lock | null = null;
    protected queueItem: QueueItem | null = null;
    private savedIngestionQueueItemID: string | null = null;
    private savedEscriptID: string | undefined = undefined;
    private savedProgramTransfer: string | undefined = undefined;
    private lastFill: Prescription = new Prescription();
    private autoRefillSubscription: AutoRefillSubscription = new AutoRefillSubscription();
    private drugAlert: string = "";
    protected showDrugChange = false;
    private dispenseError: DispenseError = new DispenseError();
    private dispenseErrors: Array<DispenseError> = [];
    private errorNote: Note = {} as Note;
    protected patientEntityType = EntityType.Patient;
    protected dispenseErrorEntityType = EntityType.DispenseError;
    private escriptResponseMessage: EscriptResponseMessage | null = null;
    private originalFillDate: Date = new Date();
    protected originPharmacy: PharmacyRecord = new PharmacyRecord();
    private tmrxCancelReason: string = "";
    private fetchEscriptCancelTokenSource: CancelTokenSource | null = null;

    // Arguments - now passed as route props
    @Prop() imageID?: string;
    @Prop() escriptID?: string;
    @Prop() queueItemID?: string;
    @Prop() programTransfer?: string;

    //Flags
    private newMode = false;
    private fromIngestion = false;
    private wasResolved: boolean = false;
    private newRefillMode = false;
    private isScheduleClassDrug = false;
    //View values
    private dispensedQuantity: number = 0;
    private originCode: number = 0;
    private statusCode: number = 0;
    private deliveryCode: number = 0;
    private dawCode: number = 0;
    private substatusCode: number | null = null;
    private statusCodes = RefillStatusCode;
    protected drugStatuses: string[] = [
        "Inactive",
        "Active",
        "Active and Preferred",
        "Blocked From Fills",
    ];
    // Buttons config
    private buttonTitles = {
        new: "",
        clear: "Clear",
        save: "Save Changes",
        cancel: "",
    };

    protected disableFieldsReason: string = "";
    protected disableBillingReason: string = "";

    protected saveButtonLabel: string = "Save changes";
    //Notes
    private addNote = "";
    private quickAnnotateText: string | null = null;
    private isQuickAnnotate = false;
    protected editPatientForm = false;
    protected showPatientForm = false;
    protected showDocuStore = false;
    protected showDoctorForm = false;
    protected showEquivalentModal = false;
    protected equivalentPrescriptionsData: EquivalentPrescription[] = [];
    protected checkingEquivalentDrugs = false; // transient state flag - true while the equivalent drug API call is in progress
    protected enableEquivalentDrugCheck = false; // configuration setting - when true, runs the equivalent drug check on save
    protected showPharmacyForm = false;
    protected editDoctorForm = false;
    private drugDetails: any = null;
    protected drugInventory: any = null;
    protected nextRefillRxId = "";
    private fieldsPending = "";
    //--------------
    protected isClaimsLoading = true;
    private initialDispensedQty = 0;
    private expiration: Date | null = null;
    private writtenDate: Date | any = null;
    private enableExpirationDateEstimation = true;
    protected loadingDrugDetails = false;
    private batchIndex: number = -1;
    private globalPreventRedirection = false;
    // validation
    private validationClasses: {
        quantity: string;
        dispense: string;
        refills: string;
        daySupply: string;
        fillDate: string;
        writtenDate: string;
        expirationDate: string;
    } = {
        quantity: "",
        dispense: "",
        refills: "",
        daySupply: "",
        fillDate: "",
        writtenDate: "",
        expirationDate: "",
    };
    private saving: boolean = false;

    protected pdfChopperVisible: boolean = false;
    private prescriptionImagePages: number = 0;

    private showDispensingFields = true;
    private disableFutureWrittenDates = false;
    private transferMyRxStatus = "";
    protected programSetFromTransfer: boolean = false;

    @Watch('prescription.transferMyRxId')
    async getTransferMyRxStatus() {
        this.transferMyRxStatus = "";

        if (!this.prescription?.transferMyRxId) return;

        const newResp = await Axios.get<any>(`/api/TransferMyRx/${this.prescription.transferMyRxId}`);
        if (!newResp.data) return;

        const json = JSON.parse(newResp.data.jsonBody);
        console.log("newResp", newResp);
        console.log("json", json);

        this.transferMyRxStatus = json.status;
        return json.status;
    }

    async cancelTransferMyRx() {
        try {
            await Axios.post(`/api/TransferMyRx/cancel/${this.prescription.transferMyRxId}`,{}, {
                params: {
                    reason: this.tmrxCancelReason
                }
            });
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Prescription,
                    this.prescription?.id?.toString(),
                    this.prescription?.toString(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    async cancelTransferMyRxWithReason() {
        try {
            await Axios.post(`/api/TransferMyRx/cancel/${this.prescription.transferMyRxId}`,{}, {
                params: {
                    reason: this.cancelReason
                }
            });
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Prescription,
                    this.prescription?.id?.toString(),
                    this.prescription?.toString(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    get showChopperButton(): boolean {
        return this.prescriptionImagePages > 1;
    }

    get overflowStyle() {
        if (this.pdfChopperVisible) return "overflow:hidden;";
        return "overflow:inherit;";
    }

    get drugNdc(): string {
        return this.drugDetails?.ndc ?? "";
    }

    get drugPackageSize(): string {
        return (this.drugDetails && `${this.drugDetails.packageSize} ${this.drugDetails.billingUnit}`) || "";
    }

    protected drugLastFilled: string = "";

    @Watch('prescriptionsInBatch')
    @Watch('patient.id')
    @Watch('prescription.rxID')
    async fetchDrugLastFilled(): Promise<string> {
        let rxId = this.prescription.rxID;
        const drugSource = this.prescription.drugSource;
        const drugId = this.prescription.packageID;
        this.drugLastFilled = "";
        if (this.patient.id == undefined || drugSource == undefined || !this.prescription.hasFullId) {
            return this.drugLastFilled;
        }
        try {
            const newResp = await Axios.get(`/api/Patient/${this.patient.id}/drug-history/${drugSource}/${drugId}/${rxId}`);
            // Only set drugLastFilled if the response contains a valid date
            if (newResp.data) {
                const momentDate = moment(newResp.data);
                if (momentDate.isValid()) {
                    this.drugLastFilled = momentDate.format("L");
                }
            }
        } catch (err) {
            //swallow
        }
        return this.drugLastFilled;
    }

    get drugStatus(): string {
        if (!isNaN(this.drug.activeStatus)) return this.drugStatuses[this.drug.activeStatus];

        if (!this.drugDetails) return "";
        if (!isNaN(this.drugDetails?.activeStatus)) return "";
        return this.drugStatuses[this.drugDetails.activeStatus];
    }

    get drugInStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated > 0;
    }

    get drugOutOfStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated <= 0;
    }

    get drugAsDrug(): Drug {
        return this.drug as Drug;
    }

    get drugSearchParams(): { getProgram: boolean; drugSource?: number } {
        const params: { getProgram: boolean; drugSource?: number } = { getProgram: true };
        // Check drug.source first (available immediately on selection),
        // then fall back to prescription.drugSource (for existing prescriptions)
        const drugSource = this.drug?.source ?? this.prescription?.drugSource;
        if (drugSource !== undefined && drugSource !== null) {
            params.drugSource = drugSource;
        }
        return params;
    }

    get dispenseErrorAlertButtonTitles(): any {
        return {
            resolve: "Resolve",
            awaitingResponse: "Awaiting Response",
        };
    }

    get showDrugAlert() {
        return this.drugAlert.length > 0;
    }

    get escriptPatient() {
        return this.escriptResponseMessage?.patient ?? null;
    }

    get escriptPrescriber() {
        return this.escriptResponseMessage?.prescriber ?? null;
    }

    get hasSuperAdminPermission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Super Admin/Prescription".toLowerCase());
    }

    get shouldBlockFutureWrittenDates() {
        return this.disableFutureWrittenDates && !this.hasSuperAdminPermission;
    }

    get disableSave(): boolean {
        if (this.hasSuperAdminPermission) return this.saving || this.checkingEquivalentDrugs;
        if (this.saving || this.checkingEquivalentDrugs) return true;
        if (this.patient?.deceased && (this.newRefillMode || this.newMode)) return true;
        return (!this.validForm || !(this.prescription && this.prescription.isActive));
    }

    get disableNew(): boolean {
        return this.disableSave;
    }

    get disableClear(): boolean {
        if (this.saving) return true;
        return !(this.newMode || (this.prescription && this.prescription.rxNumber));
    }

    get disableInternalTransfer(): boolean {
        if (this.hasClaims) return true;
        if (this.isClaimsLoading) return true;
        if (!this.prescription) return true;
        if (!this.prescription.rxNumber) return true;
        // noinspection RedundantIfStatementJS
        if (isNaN(Number(this.prescription.rfNumber))) return true;
        return false;
    }

    get showSearchBar(): boolean {
        return !(
            this.prescription?.rxNumber ||
            this.newMode ||
            this.fromIngestion ||
            this.isBatchMode ||
            this.routeRxId
        );
    }

    get shouldLockEscriptProcessingStore(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || !!(this.prescription && this.prescription.rxNumber) || ((!!this.escript.id) && (this.store.id > 0));
    }

    get routeRxId(): string | any {
        let res = null;
        const storeID = Number(this.$route.params.storeID);
        const rxNumber = Number(this.$route.params.rxNumber);
        const rfNumber = Number(this.$route.params.rfNumber);
        const rxId = this.$route.params.rxId;
        if (!isNaN(storeID) && !isNaN(rxNumber) && !isNaN(rfNumber)) {
            res = `${storeID}-${rxNumber}-${rfNumber}`;
        } else if (rxId) res = rxId;
        return res;
    }

    get drugLicenseType(): string {
        return this.drugDetails?.licenseType || "unknown";
    }

    get routeDispenseErrorId(): number | null {
        let res = null;
        if (this.$route.params.dispenseErrorId) {
            res = Number(this.$route.params.dispenseErrorId);
        } else if (this.dispenseError?.id) res = Number(this.dispenseError?.id);
        return res;
    }

    get hasClaims(): boolean {
        return this.claims.length > 0;
    }

    get disablePatient(): boolean {
        if (!!this.prescription.rfNumber) return true;

        if (this.hasSuperAdminPermission) return false;

        if (this.prescription.orders?.some(o => o.status == OrderStatus.Created)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.Processing)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.PartialPayment)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.AwaitingPriorAuthorization)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReadyForPickup)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReturnToSender)) return true;

        return false;
    }

    get isUserPrescriptionAdmin() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Prescription".toLowerCase());
    }

    get notSelectableStatus(): RefillStatusCode[] {
        const baseStatuses = [
            RefillStatusCode.TransferOut,
            RefillStatusCode.TransferIn,
            RefillStatusCode.Canceled,
        ];

        const hasVoidPermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Void Prescription"
        );

        const hasDiscontinuePermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Discontinue Prescription"
        );

        if (!hasVoidPermission && this.prescription.status !== RefillStatusCode.Void) {
            baseStatuses.push(RefillStatusCode.Void);
        }

        if (!hasDiscontinuePermission && this.prescription.status !== RefillStatusCode.Discontinued) {
            baseStatuses.push(RefillStatusCode.Discontinued);
        }

        return baseStatuses;
    }

    get disableFields(): boolean {
        if (this.hasSuperAdminPermission) return false;
        this.disableFieldsReason = "";
        const lockedForUser = !this.prescriptionLock;
        const noEntityLoaded = !(this.prescription && this.prescription.rxNumber);
        const isCanceled = this.prescription.status == RefillStatusCode.Canceled;
        let ManagerCond = false;
        if (!this.fromIngestion) {
            let dateNow: Date = new Date();
            let dateFillDate: Date = new Date(this.prescription.fillDate);
            let timeInMilliseconds: number = dateNow.getTime() - dateFillDate.getTime();
            let daysBetweenDates: number = Math.ceil(timeInMilliseconds / (1000 * 60 * 60 * 24));
            ManagerCond =
                !this.isUserPrescriptionAdmin &&
                daysBetweenDates > 15 &&
                this.prescription.status != RefillStatusCode.OnHold &&
                this.prescription.status != RefillStatusCode.AwaitingInsurance;
        }

        if (((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)) {
            this.disableFieldsReason = "((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)";
            return true;
        }

        if (this.hasClaims) {
            this.disableFieldsReason = "this.hasClaims";
            return true;
        }
        if (isCanceled) {
            this.disableFieldsReason = "isCancelled";
            return true;
        }
        if (!this.prescription.isActive) {
            this.disableFieldsReason = "!this.prescription.isActive";
            return true;
        }
        if (this.prescription.isRx30BeforeApril && !this.newRefillMode) {
            this.disableFieldsReason = "this.prescription.isRx30BeforeApril && !this.newRefillMode";
            return true;
        }
        if (ManagerCond) {
            this.disableFieldsReason = "ManagerCond";
            return true;
        }

        // noinspection UnnecessaryLocalVariableJS
        const shouldDisable =
            ((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion) ||
            this.hasClaims ||
            isCanceled ||
            !this.prescription.isActive ||
            (this.prescription?.isRx30BeforeApril && !this.newRefillMode) ||
            ManagerCond;

        // console.log("L1", ((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion));
        // console.log("L2", this.hasClaims);
        // console.log("L3", isCanceled);
        // console.log("L4", !this.prescription.isActive);
        // console.log("L5", (this.prescription?.isRx30BeforeApril && !this.newRefillMode));
        // console.log("L6", ManagerCond);
        // console.log("Main", shouldDisable);
        return shouldDisable;
    }

    get disableRefills() {
        if (this.hasSuperAdminPermission) return false;
        return this.disableFields || (this.hasShipped && this.prescription.rfNumber != 0);
    }

    get disableQuantity() {
        if (this.hasSuperAdminPermission) return false;
        if (this.disableFields) return true;
        return !!this.prescription.rfNumber;
    }

    get disableBillingComponent(): boolean {
        if (this.hasSuperAdminPermission) return false;

        this.disableBillingReason = "";

        if ((!this.prescription.isActive && !this.newMode)) {
            this.disableBillingReason = "(!this.prescription.isActive && !this.newMode)";
            return true;
        }
        if ((this.prescription.isRx30BeforeApril && !this.newRefillMode)) {
            this.disableBillingReason = "(this.prescription.isRx30BeforeApril && !this.newRefillMode)";
            return true;
        }
        const notRefillable = !this.allowNewRefill && this.newRefillMode;
        if (notRefillable) {
            this.disableBillingReason = "notRefillable";
            return true;
        }
        if (this.statusCode == this.statusCodes.OnHold) {
            this.disableBillingReason = "this.statusCode == this.statusCodes.OnHold";
            return true;
        }
        if (this.isOutOfState) {
            this.disableBillingReason = "this.isOutOfState";
            return true;
        }
        if (this.statusCode == this.statusCodes.AwaitingInsurance) {
            this.disableBillingReason = "this.statusCode == this.statusCodes.AwaitingInsurance";
            return true;
        }
        const isShippedOrVerified = (this.prescription.hasBeenShipped || this.prescription.hasBeenVerified) && !this.newRefillMode;
        if (isShippedOrVerified) {
            this.disableBillingReason = "(this.prescription.hasBeenShipped || this.prescription.hasBeenVerified) && !this.newRefillMode";
            return true;
        }
        if (this.isOlderThan14Days) {
            this.disableBillingReason = "this.isOlderThan14Days";
            return true;
        }

        return (
            (!this.prescription.isActive && !this.newMode) ||
            (this.prescription.isRx30BeforeApril && !this.newRefillMode) ||
            notRefillable ||
            this.statusCode == this.statusCodes.OnHold ||
            this.isOutOfState ||
            this.isOlderThan14Days ||
            this.statusCode == this.statusCodes.AwaitingInsurance ||
            isShippedOrVerified
        );
    }

    get disableProgramChange(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || this.programSetFromTransfer;
    }

    get hasShipped(): boolean {
        return !!(this.prescription && this.prescription.rxNumber && !this.prescription.rfNumber);
    }

    get fillsLeft(): number {
        if (!this.prescription) return 0;
        return this.prescription.refills;
        //if (!this.prescription.rfNumber) return this.prescription.refills;
        //return this.prescription.refills - this.prescription.rfNumber;
    }

    get hasHardcopyPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Hardcopy Note");
    }

    get annotationImageId(): number | null {
        // For escripts, imageId will be null - the modal will use escriptId instead
        // For regular prescriptions, use the imageID
        if (this.prescription?.escriptID) {
            return null;
        }
        return this.prescription?.imageID || null;
    }

    get annotationEscriptId(): number | null {
        return this.prescription?.escriptID || null;
    }

    get validFormForBilling() {
        // Form validation
        let valGrpEntities: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Store", !!this.prescription.storeID),
            new ValidatorGroup.Validator("Patient", !!this.prescription.patientID),
            new ValidatorGroup.Validator("Drug", !!this.prescription.packageID),
            new ValidatorGroup.Validator("Doctor", !!this.prescription.prescriberID),
            new ValidatorGroup.Validator("Program", !!this.prescription.programID),
        ]);
        const entitiesWereSelected = valGrpEntities.evaluate();

        let valGrpQuantities: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Day Supply", this.prescription.daySupply?.toString() != '' && this.prescription.daySupply >= 0),
            new ValidatorGroup.Validator("Quantity", this.prescription.quantity?.toString() != '' && this.prescription.quantity >= 0),
            new ValidatorGroup.Validator("Dispensed", this.prescription.dispensed?.toString() != '' && this.prescription.dispensed >= 0),
            new ValidatorGroup.Validator("Origin", this.prescription.originCode?.toString() != '' && this.originCode > 0),
            new ValidatorGroup.Validator("Refills", this.prescription.refills?.toString() != '' && this.prescription.refills >= 0),
        ]);
        const quantitiesWereIntroduced = valGrpQuantities.evaluate();

        let writtenDateNotInFuture = true;
        if (this.writtenDate) {
            const todayDate = new Date().setHours(0, 0, 0, 0);
            const writtenDateObj = this.writtenDate instanceof Date ? this.writtenDate : new Date(this.writtenDate);
            const compareDate = new Date(writtenDateObj.getFullYear(), writtenDateObj.getMonth(), writtenDateObj.getDate()).setHours(0, 0, 0, 0);
            const dateIsNotInFuture = !(compareDate > todayDate);
            writtenDateNotInFuture = this.shouldBlockFutureWrittenDates ? dateIsNotInFuture : true;
        }

        let valGrpDates: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Written Date", !!this.writtenDate),
            new ValidatorGroup.Validator("Expiration", !!this.expiration),
            new ValidatorGroup.Validator("Future Written Date", writtenDateNotInFuture),
        ]);
        const datesWereIntroduced = valGrpDates.evaluate();

        const remainingQuantity =
            this.prescription.remainingQuantity +
            (this.prescription.rfNumber == 0 ? 0 : this.initialDispensedQty);

        let valDispense = this.dispensedQuantity <= remainingQuantity;
        if (valDispense) {
            if (this.statusCode < 3 && this.dispensedQuantity == 0) {
                valDispense = false;
            }
        }

        const fillDateBeforeExpiration = moment(this.prescription.fillDate).isBefore(this.expiration);
        const validRefillMode = (this.allowNewRefill && this.newRefillMode) || !this.newRefillMode;
        let valGrpLongNames: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Dispensed quantity must be less than total allowed to dispense", this.dispensedQuantity <= remainingQuantity),
            new ValidatorGroup.Validator("Fill date should be before expiration date", fillDateBeforeExpiration),
            new ValidatorGroup.Validator("Mismatch in Prescriber NPI", !this.isNPIInvalid),
            new ValidatorGroup.Validator("Not Refillable", validRefillMode),
            new ValidatorGroup.Validator("Drug is not active", !!this.isDrugActive),
        ]);

        this.fieldsPending = "";
        const allGroups = new ValidatorGroup([...valGrpQuantities.validators, ...valGrpDates.validators, ...valGrpEntities.validators, ...valGrpLongNames.validators]);
        this.fieldsPending = allGroups.concatValidatorNames(false);

        if (!valDispense && quantitiesWereIntroduced) {
            if (this.dispensedQuantity === 0) {
                this.validationClasses.dispense = "has-warning";
                valDispense = true;
            } else {
                this.validationClasses.dispense = "has-danger";
            }
        }

        // Set validation styling for the written date
        if (!writtenDateNotInFuture) {
            this.validationClasses.writtenDate = "has-danger";
        } else {
            this.validationClasses.writtenDate = "";
        }

        // noinspection UnnecessaryLocalVariableJS
        const formIsValid =
            entitiesWereSelected &&
            quantitiesWereIntroduced &&
            datesWereIntroduced &&
            valDispense &&
            fillDateBeforeExpiration &&
            validRefillMode &&
            !this.isNPIInvalid &&
            this.isDrugActive;

        return formIsValid;
    }

    get validForm() {
        // Process validation
        const isOnHold = this.statusCode == RefillStatusCode.OnHold && this.substatusCode != null;
        const isAwaitingStatus = this.statusCode == RefillStatusCode.AwaitingInsurance;
        const isVoid = this.statusCode == RefillStatusCode.Void;
        const isDiscontinued = this.statusCode == RefillStatusCode.Discontinued;
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        const processIsValid =
            this.hasClaims ||
            isOnHold ||
            isVoid ||
            isDiscontinued ||
            isFillDateInTheFuture ||
            (this.prescription.isCashedOut && this.statusCode != RefillStatusCode.OnHold) ||
            isAwaitingStatus;

        //Whole validation
        return (
            this.validFormForBilling &&
            processIsValid &&
            (!this.prescription?.isRx30BeforeApril || this.newRefillMode) &&
            this.isDrugActive
        );
    }

    get isLastFill(): boolean {
        if (!this.lastFill.hasFullId) return true;
        return this.lastFill?.rxID == this.prescription?.rxID || this.newRefillMode;
    }

    get allowNewRefill(): boolean {
        if (this.patient?.deceased) return false;
        return !!(this.prescription?.isRefillable && this.isLastFill); //&& !this.refillBatchMode && !this.newRefillMode
    }

    get notAllowNewRefill(): boolean {
        return !this.allowNewRefill;
    }

    get validBillingInformation() {
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        return (
            this.hasClaims ||
            this.prescription.isCashedOut ||
            this.prescription.status == RefillStatusCode.Void ||
            this.prescription.status == RefillStatusCode.Discontinued ||
            this.prescription.status == RefillStatusCode.AwaitingInsurance ||
            (this.prescription.status == RefillStatusCode.OnHold &&
                this.prescription.subStatus != null) ||
            isFillDateInTheFuture
        );
    }

    get rxWasBilled(): boolean {
        return !!this.claims?.some((cl) => cl.transactionStatus == "Paid");
    }

    get isMultiFill(): boolean {
        return (this.dispensedQuantity || 0) > (this.prescription?.quantity || 0);
    }

    get isOutOfState(): boolean {
        if (!this.patient?.addressState || !this.store?.addressState) return false;
        if (this.patient.addressState == this.store.addressState) return false;
        return !this.store.allowedStates.some(s => this.patient.addresses.some(a => a.state?.toUpperCase().trim() == s));
    }

    get showAlertIsOutOfState(): boolean {
        return this.isOutOfState;
    }

    get isDrugActive(): boolean | null {
        if (!this.drug?.activeStatus) return null;
        return (
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.Active ||
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.ActiveAndPreferred
        );
    }

    get showInactiveDrugAlert(): boolean {
        return this.isDrugActive === false;
    }

    get sigIsSmartSig(): boolean {
        if (!this.escript?.smartSig) return false;
        return this.prescription?.directions === this.escript?.smartSig?.sigText;
    }

    get dropzoneOptions(): any {
        return {
            url: `${Axios.defaults.baseURL}/image/prescription/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: "images",
        };
    }

    get showRefillAuthRequest(): boolean {
        //If we can't fill the next fill, allow a refill auth to go.
        return (
            (this.prescription.remainingQuantity || 0) < (this.prescription.dispensed || 0) * 2 ||
            this.isDrugExpired
        );
    }

    get willAutorefill(): boolean {
        //Is subscribed to autorefill, the current date is more/equals than next process date and this page is in refill mode.
        let nextProcessDateValid = true;
        if (this.autoRefillSubscription?.nextProcessDate) {
            //nextProcessDateValid = moment().isSameOrAfter(this.autoRefillSubscription.nextProcessDate, 'day');
        }
        return !!(this.autoRefillSubscription?.id && this.newRefillMode && nextProcessDateValid);
    }

    get isDrugExpired() {
        if (!this.expiration) return false;
        return moment(moment.utc().toDate()).isSameOrAfter(this.expiration, 'days');
    }

    get isOlderThan14Days(): boolean {
        // Users with Admin/Prescription permission can edit scripts regardless of age
        if (this.isUserPrescriptionAdmin || this.newRefillMode) return false;

        const lastFillDate = moment(this.prescription.fillDate);
        const daysOld = moment().diff(lastFillDate, 'days');
        // scripts 14 or more days old require a prescription admin to do adjustments.
        return daysOld >= 14;
    }

    get showAgeAlert(): boolean {
        return this.isOlderThan14Days;
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];
    private discontinuedSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get applicableSubStatuses() {
        return this.statusCode == RefillStatusCode.OnHold ? this.onHoldSubStatuses : this.discontinuedSubStatuses;
    }

    async getSubStatusConfiguration() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
            this.discontinuedSubStatuses = resp.data.discontinuedSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
        } catch {
            //ignore
        }
    }

    get prescriptionIdentifier(): string {
        if (!this.prescription) return "";
        if (
            this.prescription.storeID == null ||
            this.prescription.rxNumber == null ||
            this.prescription.rfNumber == null
        ) {
            return "N/A";
        }
        return `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
    }

    get storeLocationInformation(): string {
        if (!this.store?.id) return "";
        return `${this.store.addressCity}, ${this.store.addressState}`;
    }

    get isNPIInvalid() {
        //Apply only if we are loading Escript or Program Transfer
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        if (!escriptId && !programTransfer) return false;

        //Invalid if there is no prescriber specified in escript or if not equal to the selected doctor.
        return !this.escriptPrescriber || this.escriptPrescriber.npiNumber != this.doctor.npiNumber;
    }

    get searchProgramGetParams() {
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        const programToAutoselect =
            (Number(escriptId) || 0) > 0 || (Number(programTransfer) || 0)
                ? this.prescription.programID
                : null;
        return {
            active: true,
            flags: "Autoselect Only",
            discard: true,
            forceProgramID: programToAutoselect || 0,
            filterByTeam: true,
        };
    }

    get searchStoreGetParams() {
        return {
            filterByTeam: true,
        };
    }

    get patientDOB(): string {
        if (!this.patient || !this.patient.dateOfBirth) return "";
        return moment(this.patient.dateOfBirth).format("L");
    }

    get escriptAllergyAlert() {
        const escriptId = this.escriptID || this.savedEscriptID;
        return (
            escriptId &&
            this.escriptResponseMessage &&
            this.escriptResponseMessage.allergies?.length > 0
        );
    }

    get additionalAlerts() {
        const alerts: Alert[] = [];

        if (this.escriptAllergyAlert) {
            alerts.push({
                name: "escriptAllergy",
                title: "Add Escript Allergies",
                message: "Add allergies found on escript before saving prescription",
                icon: "fa fa-plus",
                variant: "red"
            });
        }

        return alerts;
    }

    get isBatchOver(): boolean {
        return this.batchIndex + 1 > (this.prescriptionsInBatch?.length || 0) - 1;
    }

    get isBatchMode(): boolean {
        return !!this.prescriptionsInBatch?.length;
    }

    created() {
        this.create();
    }

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.mount();
        this.fetchDispenseErrorLabel();

        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.hotKeysListener);
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        // Cancel any pending escript request when component is destroyed
        this.fetchEscriptCancelTokenSource?.cancel("Component destroyed");

        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    // Watch for changes to route props (query parameters passed as props)
    // Each prop has its own watcher to ensure they all trigger correctly
    @Watch('queueItemID')
    onQueueItemIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('imageID')
    onImageIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('escriptID')
    onEscriptIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('programTransfer')
    onProgramTransferChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    async handleQueryPropsChange(newVal: string | undefined, oldVal: string | undefined) {
        console.debug("query props change", oldVal, newVal);

        // Skip if this is the initial component mount (both old and new are undefined)
        // But allow transition from undefined -> value (e.g., auto mode navigation from blank page)
        if (oldVal === undefined && newVal === undefined) {
            return;
        }

        // Skip if going from value -> undefined (clearing/navigating away)
        if (oldVal !== undefined && newVal === undefined) {
            return;
        }

        // Only reinitialize if we have at least one query prop set
        const hasNewPrescription = this.queueItemID || this.imageID || this.escriptID || this.programTransfer;
        if (!hasNewPrescription) {
            return;
        }

        console.log('Route query props changed, reloading prescription data');

        // Clear the form to reset all prescription data (including images)
        // This prevents stale data from previous prescription from sticking around
        this.clearForm(false, false);

        // Release all locks from previous prescription before loading new one
        await this.clearAllLocks();
        this.prescriptionLock = null;

        // Reinitialize with new data
        this.create();
        this.mount();
    }

    preventDefaultForHotkeys(e: KeyboardEvent) {
        if (e.code === "F5") e.preventDefault();
        if (e.altKey && e.key === "p") e.preventDefault();
        if (e.altKey && e.key === "r") e.preventDefault();
        if (e.altKey && e.shiftKey && e.key === "D") e.preventDefault();
        if (e.altKey && e.key === "d") e.preventDefault();
        if (e.altKey && e.key === "m") e.preventDefault();
        if (e.altKey && e.key === "i") e.preventDefault();
        if (e.altKey && e.key === "o") e.preventDefault();
        if (e.altKey && e.key === "w") e.preventDefault();
        if (e.altKey && e.key === "b") e.preventDefault();
    }

    hotKeysListener(e: KeyboardEvent) {
        if (e.code === "F5" && this.patient?.id) this.openRxHistory();
        if (e.altKey && e.key === "p") this.patientSearchComponent.focus();
        if (e.altKey && e.key === "r") this.prescriberSearchComponent.focus();
        if (e.altKey && e.shiftKey && e.key === "D") this.equivalentDrugsComponent.findEquivalentDrugs();
        if (e.altKey && e.key === "d") this.drugSearchComponent.focus();
        if (e.altKey && e.key === "m") this.programSearchComponent.focus();
        if (e.altKey && e.key === "i") this.directionsComponent.focus();
        if (e.altKey && e.key === "o") this.originComponent.focus();
        if (e.altKey && e.key === "w") this.dawComponent.focus();
        if (e.altKey && e.key === "b") this.insuranceComponent.focus();
    }

    create() {
        console.debug("starting prescription details create");
        // Props are now passed from router, already available as this.queueItemID, etc.
        // They're strings from query params, converted to numbers where needed
        this.fromIngestion = !!this.queueItemID;
        if (this.queueItemID) {
            this.savedIngestionQueueItemID = this.queueItemID;
        }
        if (this.escriptID) {
            this.savedEscriptID = this.escriptID;
        }
        if (this.programTransfer) {
            this.savedProgramTransfer = this.programTransfer;
        }

        if (this.fromIngestion) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Back to Ingestion";
            if (!this.escriptID && !this.programTransfer) this.buttonTitles.new = "Save and New";
            this.buttonTitles.save = "Save and Finish";
        }

        if (this.routeRxId || this.routeDispenseErrorId) {
            this.getPrescription(this.routeRxId);
            this.buttonTitles = {
                save: "Save Changes",
                clear: "",
                new: "",
                cancel: "",
            };

            if (this.routeDispenseErrorId) {
                this.buttonTitles.cancel = "Back to " + this.dispenseErrorLabel + " List";
            } else if (this.routeRxId) this.buttonTitles.cancel = "Back";
        }

        if (this.isBatchMode) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Next";
            this.buttonTitles.new = "";
            this.buttonTitles.save = "Save and Next";

            if (this.prescriptionsInBatch?.length) this.nextRx();
        }
    }

    mount(fetchQueueItem = true) {
        console.debug("starting prescription details mount");
        Axios.get<Record<string, boolean>>("/api/Configuration/HideDispensingFields,disableFutureWrittenDates,EnableEquivalentDrugCheck")
            .then(resp => {
                this.showDispensingFields = !resp.data.HideDispensingFields;
                this.disableFutureWrittenDates = resp.data.disableFutureWrittenDates;
                this.enableEquivalentDrugCheck = resp.data.EnableEquivalentDrugCheck;
            })
            .catch(err => console.error('Error fetching configuration:', err));
        this.getSubStatusConfiguration();
        this.getPrescriberCustomContactTemplates();
        if (this.imageID) {
            console.log("create with imageID");
            this.createPrescription(Number(this.imageID));
        }
        if (this.escriptID) {
            console.log("create with escriptID");
            this.savedEscriptID = this.escriptID;
            this.createPrescriptionEscript(Number(this.escriptID));
        }
        if (this.programTransfer) {
            console.log("create with programTransfer");
            this.savedProgramTransfer = this.programTransfer;
            this.createPrescriptionFromTransfer(Number(this.programTransfer));
        }
        if (this.queueItemID) {
            this.savedIngestionQueueItemID = this.queueItemID;
            this.addLockWithURL(`/IngestionQueue/${this.queueItemID}/Lock`, 60000);

            if (fetchQueueItem) {
                Axios.get<QueueItem>(`/IngestionQueue/${this.queueItemID}`)
                    .then((res) => {
                        if (res.data.id) this.queueItem = res.data;
                        if (this.queueItem?.rxNumber) {
                            const rxId = `${this.queueItem.storeID}-${this.queueItem.rxNumber}-${this.queueItem.rfNumber}`;
                            this.getPrescription(rxId);
                        }
                    })
                    .catch((err) => {
                        const error = err as AxiosError;
                        if (error.response?.status === 418) {
                            this.showLockedMessage(
                                EntityType.IngestionQueueItem,
                                this.queueItemID!.toString(),
                                `Queue Item ${this.queueItemID}`,
                                error.response.data
                            );
                        } else {
                            console.error("Error while loading item queue data.", {
                                err,
                                response: error.response,
                            });
                        }
                    });
            }
        }
    }

    handlePatientDocSaved() {
        if (this.popQueue()) return;
        this.backToPreviousPage(true);
    }

    backToPreviousPage(skipBillingValidation = false) {
        if (this.fromIngestion) {
            if (this.validBillingInformation || skipBillingValidation) {
                this.$router.push({ name: "Ingestion" });
            } else {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset(
                        "Complete billing information before going back to ingestion list.",
                        NotificationOptions.NotificationTypes.danger,
                    ),
                );
            }
        } else if (this.routeDispenseErrorId) {
            this.$router.push({ name: "DispenseErrors" });
        } else if (previousRoute) {
            this.$router.push(previousRoute);
        } else {
            this.$router.go(-1);
        }
    }

    claimsLoaded(billedClaims: Claim[], rx: Prescription) {
        this.claims = billedClaims;
        // Assign claims to prescription for transfer validation
        this.prescription.claims = billedClaims;
        this.isClaimsLoading = false;
        this.updatePricing(rx);
    }

    updatePricing(rx: Prescription) {
        if (rx?.rxNumber) {
            this.prescription.patientPay = rx.patientPay;
            this.prescription.cost = rx.cost;
            this.prescription.totalPrice = rx.totalPrice;
            this.prescription.tax = rx.tax;
            this.prescription.priceScheduleID = rx.priceScheduleID;
            this.prescription.status = rx.status;
            this.statusCode = rx.status;
        }
    }

    changeDispense(num: number) {
        if (this.prescription.quantity.toString().length > 11) {
            this.prescription.quantity = Number(
                this.prescription.quantity.toString().substring(0, 11),
            );
        }
        if (num.toString().length > 11) {
            num = Number(num.toString().substring(0, 11));
        }
        this.dispensedQuantity = Number(num);
        this.prescription.dispensed = Number(num);
    }

    handleDaysSupplyCalculated(calculatedDaysSupply: number) {
        // Only update days supply if it's empty or zero
        if (!this.prescription.daySupply || this.prescription.daySupply === 0) {
            this.prescription.daySupply = calculatedDaysSupply;
        }
    }

    fetchPrescription(prescription: Prescription) {
        if (prescription.rfNumber || prescription.rfNumber == 0) {
            this.getPrescription(prescription.rxID);
        }
    }

    rxTransferRefresh(transferredPrescription: Prescription) {
        this.getPrescription(transferredPrescription.rxID).then(
            this.replaceRouterParamsWithCurrentPrescription,
        );
    }

    // noinspection SpellCheckingInspection
    /**
     * rxID: Concatenated Rx Ids in the format StoreID-RxNumber-RfNumber
     * setupRefill: If true, the method will load the prescription for RxID and immediately will set up its refill in the front end ready to be edited and saved.
     */
    getPrescription(rxID: string, setupRefill = false) {
        return new Promise((resolve, reject) => {
            this.addLockWithURL(`/Prescription/${rxID}/Lock`, 60000)
                .then((result) => {
                    this.prescriptionLock = result;
                })
                .catch((error) => {
                    if (error.response && error.response.status == 418) {
                        this.showLockedMessage(
                            EntityType.Prescription,
                            rxID,
                            rxID,
                            error.response.data,
                        );
                    }
                })
                .finally(() => {
                    // linked objects will be automatically fetched by the SearchComponent when we set the objects...
                    // if we manually fetch them, they are called for twice.
                    Axios.get(`api/Prescription/${rxID}`)
                        .then((response) => {
                            if (!this.prescription) return reject();

                            this.prescription = new Prescription(response.data);
                            this.setViewValues();

                            this.initialDispensedQty = this.prescription.dispensed;

                            this.patient.id = this.prescription.patientID;
                            this.doctor.id = this.prescription.prescriberID;
                            this.store.id = this.prescription.storeID;
                            this.program.id = this.prescription.programID || 0;
                            this.originalFillDate = this.prescription.fillDate;

                            this.enableExpirationDateEstimation = false;

                            switch (this.prescription.drugSource) {
                                case DrugSource.GsddFederal:
                                    this.drug = new Drug(this.prescription.packageID);
                                    break;
                                case DrugSource.NonGsddProduct:
                                    this.drug = new NonGSDDDrug(this.prescription.packageID);
                                    break;
                                case DrugSource.CompoundProduct:
                                    this.drug = new CompoundDrug(this.prescription.packageID);
                                    break;
                            }
                            this.getDrugDetails(this.drug);

                            //Getting the information about the last fill
                            this.$http
                                .get<Prescription>(
                                    `/Prescription/last-fill/${this.prescription.patientID}/${this.prescription.storeID}/${this.prescription.rxNumber}`,
                                )
                                .then((res) => {
                                    if (res.data?.rxNumber > 0) {
                                        this.lastFill = new Prescription(res.data);
                                    }
                                })
                                .catch((err) => {
                                    console.error("Error while getting last fill info", {
                                        err,
                                        response: err?.response,
                                    });
                                    this.$notification(NotificationOptions.error(err));
                                });

                            if (this.prescription.originPharmacyId) {
                                this.$http.get<PharmacyRecord>(`/api/StoredPharmacy/${this.prescription.originPharmacyId}`)
                                    .then(pharmRecord => {
                                        this.originPharmacy = new PharmacyRecord(pharmRecord.data);
                                    }).catch(err => this.$notification(NotificationOptions.error(err)));
                            }

                            //Setting up RX form in a refill batch
                            if (setupRefill) {
                                this.fetchEscript();
                                this.newRefill();

                                // Apply MedSync short fill adjustments if provided
                                if (this.medSyncShortFillData?.isMedSyncShortFill) {
                                    this.prescription.daySupply = this.medSyncShortFillData.adjustedDaysSupply;
                                    this.prescription.dispensed = this.medSyncShortFillData.adjustedDispensed;
                                    this.dispensedQuantity = this.medSyncShortFillData.adjustedDispensed;
                                }

                                this.$http
                                    .get<AutoRefillSubscription>(
                                        `autorefill/subcriptions-by-rx/${this.prescription.storeID}/${this.prescription.rxNumber}`,
                                    )
                                    .then((res) => {
                                        if (res.data.id) {
                                            this.autoRefillSubscription =
                                                new AutoRefillSubscription(res.data);
                                        }
                                    })
                                    .catch((err) => {
                                        console.error("Error while getting autorefill sub data.", {
                                            err,
                                            response: err?.response,
                                        });
                                        this.$notification(NotificationOptions.error(err));
                                    });
                            } else {
                                this.fetchDispenseError();
                                this.fetchEscript();
                            }

                            resolve(undefined);
                        })
                        .catch((error) => {
                            console.error("Error while getting Prescription", {
                                error,
                                response: error?.response,
                            });
                            reject();
                        });
                });
        });
    }

    async fetchEscript() {
        this.fetchEscriptCancelTokenSource?.cancel("New escript fetch started");

        let rxId = this.prescription.rxID;
        if (rxId.endsWith("-")) rxId += "0";
        const capturedRxId = rxId;

        this.fetchEscriptCancelTokenSource = Axios.CancelToken.source();

        try {
            const resp = await Axios.get(`/escript/prescription/${rxId}`, {
                cancelToken: this.fetchEscriptCancelTokenSource.token
            });

            if (resp && resp.data) {
                // Validate that the response is still relevant to the current prescription
                // This prevents race conditions when rapidly navigating between prescriptions
                const currentRxId = this.prescription.rxID;
                const normalizedCurrentRxId = currentRxId.endsWith("-") ? currentRxId + "0" : currentRxId;

                if (normalizedCurrentRxId !== capturedRxId) {
                    console.warn("Escript response ignored - prescription changed during async request", {
                        requestedRxId: capturedRxId,
                        currentRxId: normalizedCurrentRxId,
                        escriptId: resp.data.eScript.id
                    });
                    return;
                }
                this.prescription.escriptID = resp.data.eScript.id;
                this.savedEscriptID = resp.data.eScript.id;
            }
        } catch (error) {
            if (Axios.isCancel(error)) {
                console.log("Escript request cancelled:", error.message);
                return;
            }
            console.error("Error while getting escript", {
                error,
                response: (error as any)?.response,
            });
        }
    }

    fetchDispenseError() {
        Axios.get(`DispenseError/By-RxId/${this.prescription.rxID}`)
            .then((response) => {
                const findCreatedCallBack = (err: DispenseError) =>
                    err.statusEnum == DispenseError.DispenseErrorStatusEnum.Created;
                const emptyDispenseError = new DispenseError(this.prescription);
                this.dispenseErrors = response.data.map((err: DispenseError) =>
                    Object.assign(new DispenseError(), err),
                );
                if (this.dispenseErrors?.some(findCreatedCallBack)) {
                    this.dispenseError =
                        this.dispenseErrors.find(findCreatedCallBack) || emptyDispenseError;
                } else {
                    this.dispenseError = emptyDispenseError;
                }
            })
            .catch((error) => {
                console.error("Error while getting error", {
                    error,
                    response: error?.response,
                });
            });
    }

    dropzoneSending(_file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    dropzoneUploadSuccess(file: any, response: any) {
        const dropzone: any = this.$refs.myVueDropzone;
        // noinspection JSUnresolvedReference
        dropzone.removeFile(file);

        // reload prescription's image
        this.prescription.imageID = response;
    }

    @Watch("searchProgramGetParams") searchProgramParamsChanged() {
        this.programSearchComponent?.reloadResults();
    }

    @Watch("writtenDate") changedWrittenDate(value: string | Date | null) {
        let val = value;
        if (!(val instanceof Date)) val = moment(val).toDate();

        this.prescription.writtenDate = val;

        if (value && this.enableExpirationDateEstimation) {
            this.estimateExpirationBasedOnPackage(this.prescription.packageID, this.prescription.drugSource, val);
        }

        if (!this.enableExpirationDateEstimation) this.enableExpirationDateEstimation = true;
    }

    @Watch("prescription.imageID")
    async fetchPrescriptionImagePages(imageID: number | null | undefined) {
        // Reset page count when imageID becomes null/undefined
        if (!imageID) {
            this.prescriptionImagePages = 0;
            return;
        }

        // Fetch image information to get the page count and set origin if from fax
        try {
            const response = await Axios.get(`image/ImageInformation/${imageID}`);
            this.prescriptionImagePages = response.data?.pageCount ?? 0;

            // If the prescription image was received via fax, set the Origin to Fax
            if (response.data?.createdBy === "system/SRFax") {
                this.originCode = OriginCode.Fax;
            }
        } catch (error) {
            // If fetching fails, reset to 0
            this.prescriptionImagePages = 0;
        }
    }

    estimateExpirationBasedOnPackage(packageId: number, source: DrugSource, writtenDate: Date) {
        const addressState = this.patient.addressState;
        let estimatedExpirationDate = moment(writtenDate);
        if (packageId) {
            Axios.get(`Drug/${packageId}/${source}/${addressState}/ScheduledExpiration`).then((response) => {
                const deaSchedule = Number(response.data);
                this.expiration = estimatedExpirationDate.add(deaSchedule, 'days').toDate();
            });
        } else {
            estimatedExpirationDate = estimatedExpirationDate.add(365, "days");
            this.expiration = estimatedExpirationDate.toDate();
        }
    }

    formattedDateAndTime(value: Date): string {
        if (!value) return "";
        const date = moment.utc(value).local();
        return date.format("lll");
    }

    @Watch("store") storeUpdate(value: any) {
        if (!value || !this.prescription) return;
        this.prescription.storeID = value.id;
    }

    patientUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.patientID = value.id;
        if (
            !this.prescription.deliveryOption &&
            (value.defaultDelivery != undefined || value.defaultDelivery != null) &&
            !this.prescription.rxNumber
        ) {
            this.deliveryCode = value.defaultDelivery;
        }
    }

    doctorUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.prescriberID = value.id;
        this.setRecommendedProgram();
    }

    programUpdate(value: any) {
        if (!this.prescription) return;
        if (!value?.id) this.program = new Program();

        this.prescription.programID = value?.id;
    }

    @Watch("drugDetails")
    drugDetailsUpdate(value: Drug | null, _oldValue: Drug | null) {
        this.imageURLs = [];
        const baseURL = Axios?.defaults?.baseURL || "";
        if (!value) return;
        const imageURI =
            baseURL + `/drug/get-image?packageID=${value.drugId}&drugSource=${value.source}`;
        this.imageURLs.push(imageURI);

        this.drug.ndc = value.ndc;
        if (this.drug instanceof Drug) this.drug.productNameLong = value.productNameLong;
    }

    @Watch("drugDetails")
    @Watch("prescription.dispensed")
    drugOrDispenseUpdate() {
        const cost = this.drugDetails?.vendors?.find((v: any) => v.displayOrder == 1)?.cost ?? 0;
        if (cost) {
            this.prescription.estimatedCost = this.prescription.dispensed * cost;
        }
    }

    @Watch("drug")
    drugUpdate(value: DrugProduct, oldValue: DrugProduct) {
        if (
            !value ||
            !value.drugId ||
            (oldValue && oldValue.drugId && +value.drugId == +oldValue.drugId && (value as Drug).productID == (oldValue as Drug).productID)
            //|| this.disableFields
        ) {
            return;
        }

        let packId: number = +value.drugId;
        if ((value as Drug).productID) this.fetchDrugCategory((value as any).productID);

        let drug: DrugProduct;
        switch (value.source) {
            case DrugSource.GsddFederal:
                drug = new Drug(packId, value);
                break;
            case DrugSource.NonGsddProduct:
                drug = new NonGSDDDrug(packId, value);
                break;
            case DrugSource.CompoundProduct:
                drug = new CompoundDrug(packId, value);
                break;
        }

        this.fetchDrug(drug);
        this.fetchDrugAlert(drug);
    }

    get showDeaAlert() {
        const needsDea = this.isScheduleClassDrug;
        const hasDea = !!this.doctor?.deaNumber;
        return needsDea && !hasDea;
    }

    getDrugDetails(drug: DrugProduct) {
        const drugId = drug.drugId;
        this.loadingDrugDetails = true;
        Axios.get<DrugProduct>(`/Drug/${drugId}`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source,
            },
        })
            .then((response) => {
                if (this.prescription?.packageID == response.data.drugId) {
                    switch (response.data.source) {
                        case DrugSource.GsddFederal:
                            this.drug = new Drug(response.data.drugId, response.data);
                            break;
                        case DrugSource.NonGsddProduct:
                            this.drug = new NonGSDDDrug(response.data.drugId, response.data);
                            break;
                        case DrugSource.CompoundProduct:
                            this.drug = new CompoundDrug(response.data.drugId, response.data);
                    }

                    this.drugDetails = response.data;
                    this.drugDetails.status = this.drug.activeStatus;
                    this.prescription.drugSource = drug.source;

                    Axios.get<any[]>(`/Drug/${drugId}/Inventory`, {
                        params: {
                            drugSource: drug.source,
                            fillingLocationsOnly: true,
                        },
                    })
                        .then((invResp) => {
                            let storeInventories = invResp.data.filter(
                                (inv) => inv.storeId == this.prescription.storeID,
                            );
                            if (storeInventories.length == 1) {
                                this.drugInventory = storeInventories[0];
                            } else {
                                this.drugInventory = storeInventories.filter(
                                    (inv) => inv.programId == this.prescription.programID,
                                )[0];
                            }
                        })
                        .catch((err) => this.$notification(NotificationOptions.error(err)));
                } else {
                    console.warn(
                        "drug details didn't match the prescription's packageID",
                        response.data,
                    );
                }
            })
            .catch((error) => {
                console.warn(error);
            })
            .finally(() => {
                this.loadingDrugDetails = false;
            });
    }

    fetchDrugAlert(drug: DrugProduct) {
        Axios.get(`/Drug/${drug.drugId}/Alert`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source,
            },
        })
            .then((response) => {
                this.drugAlert = response.data;
            })
            .catch((error) => {
                console.error("Error while getting drug alert", {
                    error,
                    response: error?.response,
                });
            });
    }

    fetchDrug(drug: DrugProduct) {
        const packId = Number(drug.drugId);
        if (this.prescription) {
            this.prescription.packageID = packId;
            this.prescription.drugSource = drug.source;
        }
        //Estimate expiration date only if we are creating a new RX
        if (!this.prescription?.rxNumber) {
            this.estimateExpirationBasedOnPackage(packId, drug.source, moment(this.writtenDate).toDate());
        }

        this.loadingDrugDetails = true;
        this.errorLoadingDrugDetails = false;
        Axios.get<DrugProduct>(`/Drug/${drug.drugId}`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source
            },
        })
            .then((response) => {
                if (!this.disableFields && this.prescription?.packageID == response.data.drugId) {
                    this.drugDetails = response.data;
                    const deaClass = this.drugDetails.deaClass ? this.drugDetails.deaClass : this.drugDetails.deaClassificationId;
                    this.isScheduleClassDrug = deaClass < 5;
                    this.setRecommendedProgram();
                }
            })
            .catch((error) => {
                console.error("Error while getting drug details", {
                    error,
                    response: error?.response,
                });
                this.errorLoadingDrugDetails = true;
            })
            .finally(() => {
                this.loadingDrugDetails = false;
            });
    }

    setRecommendedProgram() {
        // Do not attempt to set the recommended program if we've already saved the prescription before
        if (this.prescription?.rxNumber) return;

        // Do not attempt to set the recommended program if we've already set it from the transfer
        if (this.programSetFromTransfer) return;

        const programPrescriberId = this.doctor?.programId || 0;
        const drugProgramId = this.drugDetails?.program?.id || 0;

        //Prescriber program takes precedence over the drug program
        const programId = programPrescriberId || drugProgramId;
        if (!this.program) this.program = new Program();

        this.program.id = programId;
    }

    get canCreateRefill() {
        if (!this.prescription) return false;
        if (this.prescription.status == RefillStatusCode.TransferOut) return false;
        if (this.prescription.isExpired) return false;
        if (this.prescription.status == RefillStatusCode.OnHold && this.prescription.subStatus == RxDeniedSubStatusId) return false;
        if (this.prescription.status == RefillStatusCode.Discontinued) return false;
        if (!this.prescription.rfNumber && this.prescription.status == RefillStatusCode.Void) return false;
        return true;
    }

    /**
     * Prepares the form to submit a new refill for the current prescription.
     */
    newRefill() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(NotificationOptions.error('Cannot create refills for deceased patients.'));
            return;
        }

        if (!this.canCreateRefill) return;

        const nextRfNumber = (this.prescription.rfNumber || 0) + 1;
        this.nextRefillRxId = Prescription.createRxId(
            this.prescription.storeID,
            this.prescription.rxNumber,
            nextRfNumber,
        );
        this.newRefillMode = true;

        this.prescription.rfNumber = undefined;
        this.prescription.priceScheduleID = undefined;
        this.prescription.fillDate = moment().startOf("day").toDate();
        this.prescription.lotNumber = "";
        this.prescription.serialNumber = "";

        this.prescription.drugsFilledDate = null;
        this.prescription.drugExpirationDate = null;
        this.prescription.cost = null;
        this.prescription.patientPay = null;
        this.prescription.totalPrice = null;
        this.prescription.tax = null;

        const comp = this.$refs.insuranceComponent as InsuranceBillingComponent;
        if (comp) comp.fetchPatientClaims();
    }

    unVoid() {
        this.$bvModal
            .msgBoxConfirm(
                "Prescription status will be set back to Refillable and save changes if you confirm. Are you sure you want to unVoid this prescriptions?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;
                this.statusCode = RefillStatusCode.Refillable;
                this.save();
            })
            .catch((err) => {
                console.error("Error caught on unVoid button.", err);
            });
    }

    cancelPV2() {
        if (this.prescription.hasBeenShipped) {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    "Cannot cancel PV2 on shipped prescription",
                    NotificationOptions.NotificationTypes.danger,
                ),
            );
            return;
        }

        this.$bvModal
            .msgBoxConfirm(
                "By Cancelling PV2, any previous PV2 done for this RX will cancelled and inventory will be put back to stock. Are you sure?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;

                const rxId = this.prescription.rxID;
                if (!rxId) return;

                Axios.get(`/Prescription/${rxId}/cancel-pv2/`)
                    .then((response) => {
                        if (response.data.cancelled) {
                            this.prescription.pV2ID = null;
                            this.$notification(
                                NotificationOptions.notificationOptionsPreset(
                                    "PV2 was cancelled.",
                                    NotificationOptions.NotificationTypes.success,
                                ),
                            );
                        } else {
                            this.$notification(
                                NotificationOptions.errorSaveNotificationPreset("PV2 cancellation"),
                            );
                        }
                    })
                    .catch((error) => {
                        console.error("Error while cancelling PV2", {
                            error,
                            response: error?.response,
                        });
                        this.$notification(
                            NotificationOptions.errorSaveNotificationPreset(
                                "PV2 cancellation",
                                error,
                            ),
                        );
                    });
            })
            .catch((err) => {
                console.error("Error caught on cancel PV2 button.", err);
            });
    }

    newPrescription() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(
                NotificationOptions.error('Cannot create new prescriptions for deceased patients.')
            );
            return;
        }

        this.newMode = true;
        this.createPrescription();
    }

    createPrescription(imageID?: number) {
        this.prescription = new Prescription();
        this.setPrescriptionDefaultValues();

        // Setting imageID will trigger the @Watch decorator which fetches image information
        this.prescription.imageID = imageID;
    }

    setPrescriptionDefaultValues() {
        //Default values need to be assigned to another variable
        this.prescription.originalRxDate = moment().utc(true).toDate();
        this.prescription.fillDate = moment().utc(true).toDate();

        this.dawCode = DAWCode.NotSpecified;
        this.originCode = OriginCode.NotSpecified;
        this.statusCode = RefillStatusCode.Refillable;
        this.substatusCode = null;
        this.writtenDate = moment().utc(true).toDate();
    }

    createPrescriptionFromTransfer(transferId?: number) {
        if (!transferId) return;
        Axios.get(`ProgramTransfer/${transferId}`).then((resp) => {
            this.escriptResponseMessage = resp.data;
            // console.log("programTransfer resp: ", resp);
            this.prescription = new Prescription(resp.data.prescription);
            this.prescription.originalRxDate = moment().utc(true).toDate();
            this.writtenDate = moment(resp.data.prescription.writtenDate).toDate();

            this.prescription.fillDate = moment().utc(true).toDate();
            this.prescription.dispensed = this.prescription.quantity;
            this.dispensedQuantity = this.prescription.dispensed;
            this.originCode = OriginCode.Transfer;
            this.prescription.originCode = OriginCode.Transfer;

            this.drug = new Drug(this.prescription.packageID);
            this.drug.source = this.prescription.drugSource;
            this.getDrugDetails(this.drug);

            this.program = new Program(resp.data.programObj);
            if (resp.data.programObj) {
                this.programSetFromTransfer = true;
            } else {
                this.setRecommendedProgram();
            }
            this.store = new Store(resp.data.storeObj);
            this.patient = new Patient(resp.data.patientObj);
            this.doctor = new Prescriber(resp.data.prescriberObj);

        });
    }

    createPrescriptionEscript(escriptID?: number) {
        console.log("Creating prescription from escript");

        if (escriptID) {
            this.originCode = OriginCode.Escript;
            Axios.get<EscriptResponse>(`/escript/${escriptID}`)
                .then((resp) => {

                    this.escriptResponseMessage = resp.data.message;
                    this.escript = new Escript(resp.data.eScript);

                    if (resp.data.message?.kind === "NewRx") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxRenewalResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);

                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxChangeResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxTransfer") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Transfer;
                        this.prescription.daySupply = resp.data.message.drugs.daysSupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.originPharmacy = Object.assign(new PharmacyRecord(), resp.data.message.originPharmacyObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.exception) {
                        const errorMsg = "There was an exception while loading Escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp.data.message.exception);
                    } else {
                        const errorMsg = "Unknown error, not able to load escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp);
                    }
                })
                .catch((err) => {
                    const errorMsg = "Error while loading Escript data";
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            errorMsg,
                            NotificationOptions.NotificationTypes.danger,
                        ),
                    );
                    console.log(errorMsg, {
                        err,
                        response: err.response,
                    });
                });
        }
    }

    startEditPatient() {
        this.editPatientForm = true;
    }

    clearForm(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.clearPrescription(continueSameImage, clearQueryParams);
        this.programSetFromTransfer = false;
        this.drugCategories = [];
        this.drugAlert = "";
        this.claims = [];
        (this.$refs.insuranceComponent as any).clearComponent();
        this.setPrescriptionDefaultValues();

        this.enableExpirationDateEstimation = true;
        this.showDocuStore = false;
        this.initialDispensedQty = 0;

        this.newMode = false;
        this.newRefillMode = false;
        this.$nextTick(() => {
            this.$forceUpdate();
        });
    }

    clearPrescription(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.prescription = new Prescription();

        this.lastFill = new Prescription();

        if (!continueSameImage) {
            this.patient = new Patient();
            this.doctor = new Prescriber();
            this.store = new Store();
            this.savedEscriptID = undefined;
            this.savedProgramTransfer = undefined;
            this.savedIngestionQueueItemID = null;
        }

        this.program = new Program();
        this.drug = new Drug();
        this.expiration = null;
        this.writtenDate = null;
        this.drugDetails = null;
        this.dispenseError = new DispenseError();
        this.dispenseErrors = [];

        this.dispensedQuantity = 0;

        if (clearQueryParams) {
            // when we clear, we need to clear the imageID query if it exists.
            this.clearQuery();
        }
    }

    undoChanges() {
        this.clearForm();
        window.scrollTo(0, 0);
    }

    setViewValues(_isEscript = false) {
        this.prescription.fillDate = moment(this.prescription.fillDate).toDate();

        this.dawCode = this.prescription.dawCode;
        this.originCode = this.prescription.originCode;
        this.statusCode = this.prescription.status;
        this.substatusCode = this.prescription.subStatus ?? null;
        this.deliveryCode = this.prescription.deliveryCode;
        this.dispensedQuantity = this.prescription.dispensed;

        if (this.prescription.expirationDate) {
            this.expiration = moment(this.prescription.expirationDate).toDate();
        } else {
            this.expiration = null;
        }
        this.writtenDate = moment(this.prescription.writtenDate).format();
    }

    assignViewValues() {
        this.prescription.dawCode = this.dawCode;
        this.prescription.originCode = this.originCode;
        this.prescription.status = this.statusCode;
        this.prescription.subStatus =
            this.statusCode == RefillStatusCode.OnHold ? this.substatusCode :
                this.statusCode == RefillStatusCode.Discontinued ? this.substatusCode :
                    null;
        this.prescription.deliveryCode = this.deliveryCode;
        this.prescription.expirationDate = this.expiration;
        this.prescription.writtenDate = moment.utc(this.writtenDate).toDate();
    }

    @Watch("statusCode") statusCodeUpdate(newValue: number, oldValue: number) {
        if (
            (oldValue === RefillStatusCode.OnHold ||
                oldValue === RefillStatusCode.AwaitingInsurance) &&
            newValue !== RefillStatusCode.OnHold &&
            newValue !== RefillStatusCode.AwaitingInsurance
        ) {
            this.prescription.fillDate = moment().utc(true).toDate();
        }
    }

    openPdfAnnotator() {
        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        // Reset quick annotate state for manual annotation
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;

        this.$bvModal.show('prescription-pdf-annotation');
    }

    openQuickAnnotate() {
        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        if (!this.addNote.trim()) {
            this.$notification(NotificationOptions.error("Please enter a note to add."));
            return;
        }

        // Set up quick annotate with the note text
        this.quickAnnotateText = this.addNote.trim();
        this.isQuickAnnotate = true;

        this.$bvModal.show('prescription-pdf-annotation');
    }

    async onPdfAnnotationSaved(payload: { oldImageId: number | null; newImageId: number; escriptId: number | null }) {
        if (!this.prescription) return;

        // Clear the quick annotate state and the note textarea
        if (this.isQuickAnnotate) {
            this.addNote = "";
            this.quickAnnotateText = null;
            this.isQuickAnnotate = false;
        }

        // Handle eScript annotation - just refresh the display, no prescription update needed
        if (payload.escriptId) {
            // Refresh the eScript image display
            if (this.prescriptionImageComponent) {
                (this.prescriptionImageComponent as any).fetchPrescriptionEscriptResponse(payload.escriptId);
            }
            this.$notification(NotificationOptions.success("eScript annotation saved successfully."));
            return;
        }

        // Handle regular image annotation - update the prescription with the new image ID
        if (payload.newImageId) {
            try {
                const previousImageId = this.prescription.imageID;

                // Update the local prescription imageID
                this.prescription.imageID = payload.newImageId;

                // Save the prescription to persist the new imageID
                await Axios.post("/Prescription/", {
                    ...this.prescription,
                    fillDate: moment(this.prescription.fillDate).format('YYYY-MM-DD'),
                }, {
                    params: { createNewRefill: false },
                });

                // Refresh the image display
                if (this.prescriptionImageComponent) {
                    (this.prescriptionImageComponent as any).fetchPrescriptionImageSource(
                        `image/${payload.newImageId}`
                    );
                }

                this.$notification(NotificationOptions.success("Annotated PDF saved and linked to prescription."));
            } catch (err: any) {
                console.error("Failed to update prescription with new image:", err);
                // Revert the imageID on failure
                this.prescription.imageID = payload.oldImageId ?? undefined;
                this.$notification(NotificationOptions.error(err?.response?.data || "Failed to update prescription with annotated image."));
            }
        }
    }

    onPdfAnnotationClosed() {
        // Reset quick annotate state when modal is closed
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;
    }

    saveAndContinue() {
        this.savePrescription();
    }

    saveAndBill(billInsuranceCallback: Function) {
        if (this.validFormForBilling) {
            this.savePrescription(false, billInsuranceCallback);
        } else {
            billInsuranceCallback(null, true);
            const message =
                "Not possible to save and bill. Please, first fill all required fields." +
                (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
            this.$bvModal.msgBoxOk(message, {
                title: "Not possible to save and bill.",
                size: "sm",
                buttonSize: "sm",
                okVariant: "danger",
                footerClass: "p-2",
                hideHeaderClose: false,
                centered: true,
            });
        }
    }

    saveAndFinish() {
        if (this.dispenseError?.id) {
            this.$bvModal.show("modal_save_confirmation");
        } else {
            if (this.validFormForBilling) {
                this.checkEquivalentPrescriptions();
            } else {
                const message =
                    "Not possible to save. Please, first fill all required fields." +
                    (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
                this.$bvModal.msgBoxOk(message, {
                    title: "Not possible to save.",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                });
            }
        }
    }

    async checkEquivalentPrescriptions() {
        // Skip equivalent check if not enabled in configuration
        if (!this.enableEquivalentDrugCheck) {
            this.savePrescription(true);
            return;
        }

        if (!this.prescription.packageID || !this.prescription.patientID) {
            this.savePrescription(true);
            return;
        }

        this.checkingEquivalentDrugs = true;
        try {
            const response = await Axios.get<EquivalentPrescription[]>(
                '/Prescription/EquivalentPrescriptions',
                {
                    params: {
                        patientId: this.prescription.patientID,
                        packageId: this.prescription.packageID,
                        storeId: this.prescription.storeID,
                        rxNumber: this.prescription.rxNumber,
                    },
                }
            );

            if (response.data.length > 0) {
                this.equivalentPrescriptionsData = response.data;  // Store data to pass to modal
                this.showEquivalentModal = true;  // Only show if equivalents exist
            } else {
                this.savePrescription(true);  // Skip directly to save
            }
        } catch (error) {
            console.error('Error checking equivalents:', error);
            this.savePrescription(true);  // On error, proceed with save
        } finally {
            this.checkingEquivalentDrugs = false;
        }
    }

    onEquivalentDiscontinued(result: any) {
        this.equivalentPrescriptionsData = [];
        this.savePrescription(true);
    }

    onEquivalentSkipped() {
        this.equivalentPrescriptionsData = [];
        this.savePrescription(true);
    }

    modalSaveAndResolve(_dispenseErrorId: number) {
        if (this.wasResolved) {
            // Mark the error as resolved.
            Axios.post(`DispenseError/resolve/${this.dispenseError.id}`)
                .then((response) => {
                    this.dispenseError = Object.assign(new DispenseError(), response.data);
                    console.log("Error was marked as resolved", this.dispenseError);

                    this.fetchDispenseError();
                    this.savePrescription(true);
                })
                .catch((error) => {
                    const errorMsg = "Error while resolving " + this.dispenseErrorLabel;
                    this.$notification(NotificationOptions.error(error));
                    console.log(errorMsg, {
                        error,
                        response: error?.response,
                    });
                })
                .finally(() => {
                    this.$bvModal.hide("modal_save_confirmation");
                });
        } else {
            this.savePrescription();
            this.$bvModal.hide("modal_save_confirmation");
        }
    }

    updateSaveButtonLabel() {
        let firstAction = "";
        let secondAction = "";
        let mainAction = "Save changes";
        if (this.errorNote.body && this.wasResolved) {
            firstAction = ", add note and";
            secondAction = "resolve";
        } else if (this.errorNote.body || this.wasResolved) {
            mainAction += " and ";
            if (this.errorNote.body) secondAction = "add note ";
            if (this.wasResolved) secondAction = "resolve";
        } else {
            this.saveButtonLabel = mainAction;
        }

        this.saveButtonLabel = `${mainAction}${firstAction} ${secondAction}`;
    }

    noteChanged(newNote: string) {
        this.errorNote.body = newNote;
        this.updateSaveButtonLabel();
    }

    @Watch("wasResolved") wasResolvedChanged() {
        this.updateSaveButtonLabel();
    }

    /**
     * finish: If true, it indicates to finish creating new Rx for the same script image coming from the ingestion.
     * billPlanCallback: Function callback sent from InsuranceBillingComponent that allows us to save prescription before billing an insurance.
     * goNextRx: If true, the prescription is saved and the next Rx stored in this.prescriptionsInBatch will be loaded in the form.
     */
    savePrescription(
        finish: boolean = false,
        billPlanCallback: Function | null = null,
        goNextRx: boolean = false,
    ) {
        if (!this.prescription || this.saving) return;

        // Check if the patient is deceased when creating a new prescription or refill
        if (this.patient?.deceased && (this.newMode || this.newRefillMode)) {
            this.$notification(NotificationOptions.error('Cannot save prescriptions for deceased patients.'));
            return;
        }

        if (this.drug instanceof Drug) {
            this.prescription.productNameShort = this.drug.productNameLong;
        }
        this.prescription.quantity = this.prescription.quantity || 0;
        this.prescription.dispensed = this.prescription.dispensed || 0;
        this.assignViewValues();
        if (this.queueItemID && !this.prescription.rxNumber) {
            this.saving = true;
            this.addLockWithURL(`/IngestionQueue/${this.queueItemID}/lock`, 60000)
                .then((_result) => {
                    this.saving = false;
                    this.saveAfterCheck(finish, billPlanCallback, goNextRx);
                })
                .catch((error) => {
                    if (error.response && error.response.status == 418) {
                        console.log(`QueueItem is locked`);
                        console.log(error.response.data);
                        this.showLockedMessage(
                            EntityType.IngestionQueueItem,
                            this.queueItemID!.toString(),
                            this.queueItemID!.toString(),
                            error.response.data,
                        );
                    } else {
                        this.$notification(NotificationOptions.error(error));
                    }
                    this.saving = false;
                });
            //do lock check,
            //then do save
        } else {
            this.saveAfterCheck(finish, billPlanCallback, goNextRx);
        }
    }

    popQueue() {
        if (!(this.$store.queueItems?.length ?? 0)) return false;

        const nextItem = this.$store.queueItems?.pop();

        // Build query object, only including properties with actual values
        const query: Record<string, string> = {};
        if (nextItem?.imageID) query.imageID = String(nextItem.imageID);
        if (nextItem?.escriptID) query.escriptID = String(nextItem.escriptID);
        if (nextItem?.id) query.queueItemID = String(nextItem.id);
        if (nextItem?.programTransferId) query.programTransfer = String(nextItem.programTransferId);

        this.$router.replace({
            name: "Prescription",
            query,
        });

        return true;
    }

    async saveAfterCheck(
        finish: boolean = false,
        billPlanCallback: Function | null = null,
        goNextRx: boolean = false,
    ) {
        if (this.saving) return;

        if (this.prescription.rxID != "--") {
            let dateNow: Date = new Date();
            let dateFillDate: Date = new Date(this.prescription.fillDate);
            let timeInMilliseconds: number = dateNow.getTime() - dateFillDate.getTime();
            let daysBetweenDates: number = Math.ceil(timeInMilliseconds / (1000 * 60 * 60 * 24));
            if (
                !this.isUserPrescriptionAdmin &&
                daysBetweenDates > 15 &&
                this.prescription.fillDate != this.originalFillDate
            ) {
                this.$bvModal.msgBoxOk(`You cannot change the fill date to older than 14 days ago.`);
                return;
            }
        }

        if (this.showDeaAlert) {
            try {
                const deaResponse = await this.$bvModal.msgBoxConfirm("The prescriber has no DEA number assigned and this prescription is for a controlled substance. Continue saving?");
                if (!deaResponse) return;
            }
            catch {

            }
        }

        this.saving = true;
        if (this.getErrorValidation()) {
            Axios.post("/Prescription/", { ...this.prescription, fillDate: moment(this.prescription.fillDate).format('YYYY-MM-DD'), dispensingItems: this.localDispenseItems }, {
                params: { createNewRefill: this.newRefillMode },
            })
                .then((response) => {
                    this.prescription = Object.assign(new Prescription(), response.data);
                    this.$notification(
                        NotificationOptions.successSaveNotificationPreset(
                            `Prescription ${this.prescription.rxID}`,
                        ),
                    );

                    // Capture queueItemID before route change to prevent it from becoming undefined
                    // Falls back to savedIngestionQueueItemID which persists across multiple save operations
                    const savedQueueItemID = this.queueItemID || this.savedIngestionQueueItemID;

                    new Promise((resolve, reject) => {
                        /**
                         * When on an Ingestion, the route will be replaced by the newly created RX
                         * and will push a new URL on the browsing history for every new RX created
                         * from the same image.
                         */
                        if (!this.isBatchMode && this.fromIngestion) {
                            const currentRoute = this.$route;
                            const route = {
                                name: "PrescriptionDetails",
                                params: {
                                    storeID: this.prescription.storeID.toString(),
                                    rxNumber: this.prescription.rxNumber.toString(),
                                    rfNumber: this.prescription.rfNumber?.toString() ?? "",
                                },
                                query: { preserveScroll: "1" },
                            };
                            if (currentRoute.query.queueItemID) {
                                this.$router.replace(route);
                            } else if (currentRoute.name == "PrescriptionDetails") {
                                this.$router.push(route);
                            }
                        }

                        if (
                            savedQueueItemID &&
                            this.prescription &&
                            this.prescription.storeID &&
                            this.prescription.rxNumber &&
                            (this.prescription.rfNumber || 0) >= 0
                        ) {
                            const queueItem: QueueItem = {
                                id: +savedQueueItemID,
                                storeID: +this.prescription.storeID,
                                rxNumber: +this.prescription.rxNumber,
                                rfNumber: +(this.prescription.rfNumber || 0),
                            } as QueueItem;

                            // Complete ingestion
                            Axios.post<QueueItem[]>(`/IngestionQueue/`, [queueItem])
                                .then(async (response) => {
                                    console.log(`IngestionQueue marking complete:`, response);

                                    const navigatedToNextItem = await this.handleAutoModeCompletion();

                                    resolve({ response, navigatedToNextItem });
                                })
                                .catch((error) => {
                                    const errorMsg = `IngestionQueue marking complete error:`;
                                    console.error(errorMsg, {
                                        error,
                                        response: error?.response,
                                    });
                                    this.$notification(
                                        NotificationOptions.error(error),
                                    );
                                    reject(error);
                                });
                        } else {
                            if (this.routeRxId && finish) {
                                // If this page was shown by clicking a details button from another page, we go back to the previous page.
                                this.backToPreviousPage();
                            } else {
                                console.error(
                                    "Something went wrong while identifying the case of saving",
                                    {
                                        rxId: this.prescription.rxID,
                                        fromIngestion: this.fromIngestion,
                                        queueItemID: savedQueueItemID,
                                        routeRxId: this.routeRxId,
                                        finish,
                                    },
                                );
                                reject();
                            }
                        }
                    }).then((result) => {
                        if (finish) {
                            const didPopQueueItem = this.popQueue();

                            // Only navigate back if NEITHER auto mode nor legacy queue navigated to next item
                            if (!result?.navigatedToNextItem && !didPopQueueItem) {
                                // when we clear, we need to clear the imageID query if it exists.
                                this.clearQuery();
                                // Back to ingestion queue screen - skip billing validation since we already saved successfully
                                this.backToPreviousPage(true);
                            }
                        } else if (!finish) {
                            //Save and continue with the same image
                            if (this.fromIngestion && !billPlanCallback) {
                                //When "Saving and New", We should keep the Store, patient and doctor information the same.
                                const continueSameImage = true;
                                const preservedOriginCode = this.originCode;
                                this.clearForm(continueSameImage);
                                this.mount(finish);

                                this.prescription.storeID = Number(this.store.id);
                                this.prescription.patientID = Number(this.patient.id);
                                this.prescription.prescriberID = Number(this.doctor.id);
                                this.originCode = preservedOriginCode;

                                // Restore the image from the queue item for the new prescription
                                if (this.queueItem?.imageID) {
                                    this.prescription.imageID = this.queueItem.imageID;
                                }

                                this.$notification(
                                    NotificationOptions.notificationOptionsPreset(
                                        "You can start introducing information for the new RX with the same image.",
                                        NotificationOptions.NotificationTypes.info,
                                    ),
                                );
                            }
                        }
                    });

                    //Prevent redirection to the previous page after submitting answer to custom questions:
                    // - Saving RX was triggered by billing component
                    // - Finish is false which means we continue with the same RX image
                    // - If we are in a refill batch, and we need to go to the next RX.
                    const preventRedirection = !!billPlanCallback || !finish || goNextRx;
                    this.submitQuestionaries(this.prescription, preventRedirection);

                    //If the Next button was clicked
                    if (goNextRx) {
                        if (this.willAutorefill) {
                            this.$emit("batchFinished", this.prescription);
                        }
                        this.nextRx();
                        return;
                    }

                    this.newMode = false;
                    this.newRefillMode = false;

                    if (billPlanCallback) {
                        billPlanCallback(this.prescription);
                        return;
                    }
                })
                .catch((error: AxiosError) => {
                    if (billPlanCallback) billPlanCallback(null, true);

                    // Handle duplicate prescription from same image
                    if (error.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_IMAGE_PRESCRIPTION') {
                        this.$bvModal.msgBoxOk(
                            'This prescription has already been created from this image by another user. Please close this page and check the existing prescription.',
                            { title: 'Duplicate Prescription', okVariant: 'warning' }
                        );
                        return;
                    }

                    this.$notification(NotificationOptions.error(error));
                })
                .finally(() => {
                    this.saving = false;
                });
        }
    }

    getErrorValidation() {
        if (this.prescription.daySupply === 0) {
            return true;
        }

        if (this.validationClasses.daySupply === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for days supply"));
                this.saving = false;
            });
            return false;
        }

        if (this.validationClasses.refills === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for refills"));
                this.saving = false;
            });
            return false;
        }

        return true;
    }

    clearQuery() {
        // when we clear, we need to clear the imageID query if it exists.
        if (Object.keys(this.$route.query).length) {
            console.log("clearing query");
            this.$router.replace({
                name: this.$route.name,
                query: {},
            } as Location);
        }
    }

    onPatientCreated(newPatient: Patient) {
        this.editPatientForm = false;
        this.showPatientForm = false;
        this.patient = newPatient;
        this.$bvModal.hide("create-patient");
    }

    onDoctorCreated(newDoctor: Prescriber) {
        this.showDoctorForm = false;
        this.editDoctorForm = false;
        this.doctor = newDoctor;
        this.$bvModal.hide("create-doctor");
    }

    showPrescriptionForm(close?: string) {
        switch (close) {
            case "hidePatientForm":
                this.editPatientForm = false;
                this.showPatientForm = false;
                this.patient = new Patient();
                break;
            case "hideDoctorForm":
                this.showDoctorForm = false;
                this.editDoctorForm = false;
                this.doctor = new Prescriber();
                break;
        }
        this.showDocuStore = false;
    }

    replaceRouterParamsWithCurrentPrescription() {
        if (this.routeRxId == null) return;

        if (!isNaN(Number(this.$route.params.storeID))) {
            this.$router.replace({
                name: "PrescriptionDetails",
                params: {
                    rfNumber: this.prescription.rfNumber?.toString() ?? "",
                    rxNumber: this.prescription.rxNumber?.toString() ?? "",
                    storeID: this.prescription.storeID.toString(),
                },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
        if (this.$route.params.rxId) {
            this.$router.replace({
                name: "PrescriptionDetailsId",
                params: { rxId: this.prescription.rxID },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
    }

    transferRx() {
        console.log("route: ", this.$route);
        Axios.post(`/Store/${this.newStore?.id}/transfer`, this.prescription)
            .then((response) => {
                this.prescription = Object.assign(new Prescription(), response.data);

                this.$notification(
                    NotificationOptions.successSaveNotificationPreset("Prescription Transferred"),
                );

                this.replaceRouterParamsWithCurrentPrescription();
            })
            .catch((error: AxiosError) => {
                if (error) {
                    this.$notification(
                        NotificationOptions.errorSaveNotificationPreset("Internal Transfer", error),
                    );
                    console.error("Internal Transfer - ", {
                        error,
                        response: error?.response,
                    });
                }
            });
        this.$bvModal.hide("transfer-rx");
    }

    equivalentDrugsSearched(drugs: any) {
        this.equivalentDrugs = drugs;
    }

    save() {
        if (this.isBatchMode) {
            this.saveAndNext();
        } else {
            this.saveAndFinish();
        }
    }

    cancel() {
        if (this.isBatchMode) {
            this.nextRx();
        } else {
            this.backToPreviousPage();
        }
    }

    //**BATCH MODE FUNCTIONS */
    saveAndNext() {
        this.savePrescription(true, null, true);
    }

    nextRx() {
        const thisRx = this.prescription;
        if (this.batchIndex >= 0) {
            this.clearForm();
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    `Next Rx in batch...`,
                    NotificationOptions.NotificationTypes.info,
                ),
            );
        }

        if (!this.isBatchOver) {
            this.batchIndex++;
            const nextRxId = Object.assign(
                new Prescription(),
                this.prescriptionsInBatch[this.batchIndex],
            ).rxID;
            this.getPrescription(nextRxId, this.refillBatchMode);
            this.scrollToBeginning();

            if (this.isBatchOver) {
                this.buttonTitles.save = "Save and Finish";
                this.buttonTitles.cancel = "";
            }
        } else {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    `Batch was finished.`,
                    NotificationOptions.NotificationTypes.info,
                ),
            );
            this.$emit("batchFinished", thisRx);
        }
    }

    scrollToBeginning() {
        const switchId = "rx-modal___BV_modal_header_";
        const switch_dom = document.getElementById(switchId);
        if (switch_dom) switch_dom.scrollIntoView({ block: "start" });
    }

    //**CUSTOM QUESTIONS METHODS*/
    fetchDrugCategory(productID: number | string = 0) {
        if (!(this.drug?.drugId || productID)) return;

        productID = productID || this.drug?.drugId;
        Axios.get<Array<DrugCategory>>(`Drug/${productID}/${this.drug.source}/categories`)
            .then((response) => {
                this.drugCategories = response.data.map((r) =>
                    Object.assign(new DrugCategory(), r),
                );
            })
            .catch((error) => {
                console.error("Error while getting drug categories", {
                    error,
                    response: error?.response,
                });
            });
    }

    questionarySaved(_categoryId: number, preventRedirection: boolean = false) {
        if (!(this.globalPreventRedirection || preventRedirection)) {
            this.globalPreventRedirection = true;
            this.backToPreviousPage();
        }
    }

    updateQueueItems(queueItems: QueueItem[]) {
        console.log("queueItems: ", queueItems);
        console.log("this.$store.queueItems: ", JSON.stringify(this.$store.queueItems));

        if (this.$store.queueItems?.length ?? 0 > 0) {
            this.$store.queueItems?.push(...queueItems);
        } else {
            this.$store.queueItems = queueItems;
        }
        console.log("this.$store.queueItems: ", JSON.stringify(this.$store.queueItems));
        this.popQueue();
    }

    redrawEscript() {
        const escriptId = this.escriptID || this.savedEscriptID;
        if (!escriptId) {
            console.error('No escriptID available for redraw');
            return;
        }
        Axios.get(`Escript/Redraw/${escriptId}`)
            .then((resp) => {
                console.log("escript Redraw response: ", resp);
                window.location.reload();
            })
            .catch((_err) => {
            });
    }

    submitQuestionaries(rx: Prescription, preventRedirection: boolean = false) {
        const refs = this.$refs;
        this.drugCategories?.forEach((cat) => {
            const comp = (refs[`questionary_${cat.id}`] as Array<any>)[0] as QuestionaryComponent;
            if (comp.save) comp.save(rx, preventRedirection);
        });
    }

    //Patient Notes
    async refreshPatientNotes(_newNote: Note) {
        if (this.$refs.patientNotesList instanceof NotesList) {
            await (this.$refs.patientNotesList as NotesList).loadNotes();
        }
    }

    openRxHistory() {
        const modal = this.$refs["patient_rx_history_modal"] as PatientRxHistoryModalComponent;
        if (modal) {
            modal.openRxHistory();
        } else {
            console.error("Reference dont exist for the component", modal);
        }
    }

    @Watch("prescription")
    prescriptionUpdated(rx: Prescription) {
        if (
            rx.programName?.includes("COVID") &&
            rx.updatedBy == "PatientWeb" &&
            this.notAllowNewRefill &&
            this.refillBatchMode
        ) {
            console.log(
                "prescription's programName includes 'COVID' and was updated by PatientWeb. Lets skip it.",
            );
            this.nextRx();
        }
    }

    @Watch("expiration")
    expirationDateUpdate(val: Date, _oldVal: Date) {
        // noinspection PointlessBooleanExpressionJS,SuspiciousTypeOfGuard
        if (!(val instanceof Date) || isNaN(val as any)) return;
        this.validationClasses.expirationDate = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.fillDate")
    @Watch("expiration")
    fillDateUpdate() {
        const fillDate = this.prescription.fillDate;
        // Don't apply validation styling until both dates are available
        if (!fillDate || !this.expiration) {
            this.validationClasses.fillDate = "";
            return;
        }
        const valid = moment(fillDate).isSameOrBefore(this.expiration);
        this.validationClasses.fillDate = valid ? "has-success" : "has-danger";
    }

    @Watch("prescription.daySupply")
    daySupplyUpdate(val: number, _oldVal: number) {
        this.validationClasses.daySupply = val && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("prescription.refills")
    refillsUpdate(val: number, _oldVal: number) {
        this.validationClasses.refills = val != null && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("dispensedQuantity")
    dispenseUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.dispense = "has-warning";
            return;
        }
        this.validationClasses.dispense = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.quantity")
    quantityUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.quantity = "has-warning";
            return;
        }
        this.validationClasses.quantity = val ? "has-success" : "has-danger";
    }

    updateAlerts() {
        this.entityAlertsComponent?.refresh();
    }

    startSurescriptsElectronicPriorAuthorization() {
        Axios.post("PriorAuthorization/Create/Surescripts", this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    startSurescriptsElectronicPriorAuthorizationExpedited() {
        Axios.post("PriorAuthorization/Create/Surescripts/Expedited", this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    onPharmacyFormClose() {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord();
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord(newPharma);
    }

    contactTypeFormatter(value: Number): string {
        switch (value) {
            case 0:
                return "Text";
            case 1:
                return "Phone";
            case 2:
                return "Email";
            case 3:
                return "Auto Outbound";
            case 4:
                return "Auto Fax";
        }
        return "Unknown";
    }

    get prescriptionContactFields() {
        return [
            {
                key: "contactType",
                formatter: this.contactTypeFormatter,
            },
            {
                key: "contactDate",
                formatter: (value: Date) => moment(value).format("l LT"),
                sortable: true,
            },
            "contactMessage",
        ];
    }

    async prescriptionContacts(_ctx: BvTableCtxObject) {
        try {
            const response = await Axios.get<Array<Prescription>>(`/api/Prescription/${this.prescription.rxID}/contacts`);
            return response.data.map((r) => new Prescription(r));
        } catch (error) {
            return [];
        }
    }

    async openDocument(pd: PatientDocument) {
        try {
            const res = await Axios.get(`image/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting image to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Watch('prescription')
    @Watch('patient.primaryLanguage')
    @Watch('prescription.directions')
    @Watch('drugNdc')
    @Watch('drug.productNameShort')
    async fetchTranslations() {
        this.directionsTranslations = {} as any;
        if (this.patient.primaryLanguage == 0) return;
        if (!(this.prescription.rxNumber > 0)) return;
        if (this.prescription.isExpired) return;
        if (!this.drugNdc) return;
        if (!this.drug.productNameShort) return;

        try {
            this.loadingDirections = true;
            const resp = await Axios.get(`/api/SigTranslation/Translations`, {
                params: {
                    directions: this.prescription.directions,
                    drugName: this.drug.productNameShort,
                    ndc11: this.drugNdc,
                },
            });
            this.directionsTranslations = resp.data;
        } catch (err) {
            // ignore
        }
        this.loadingDirections = false;
    }

    @Watch('prescription')
    handleDispenseItemsChange() {
        const hasDispenseItems = !!this.prescription?.dispensingItems?.length;
        if (hasDispenseItems) {
            this.localDispenseItems = [...this.prescription.dispensingItems!];
            return;
        }

        const hasLegacyDispenseData = this.prescription.serialNumber || this.prescription.lotNumber || this.prescription.drugExpirationDate;
        if (hasLegacyDispenseData) {
            this.localDispenseItems = [
                {
                    lotNumber: this.prescription.lotNumber,
                    serialNumber: this.prescription.serialNumber,
                    expirationDate: this.prescription.drugExpirationDate,
                    quantity: null,
                },
            ];
        } else {
            this.localDispenseItems = [];
        }
    }

    async translateDirections() {
        if (!this.selectedTranslation) {
            this.$notification(NotificationOptions.error("Select directions to translate."));
            return;
        }
        this.loadingDirections = true;
        try {
            const config = {
                params: {
                    directions: this.prescription.directions,
                    patientId: this.patient.id,
                },
            };
            const resp = await Axios.get<string>(`/api/SigTranslation/Translations/${this.selectedTranslation}`, config);
            const translation = this.directionsTranslations.paraphrases.find(p => p.id == this.selectedTranslation);
            const baseDirections = translation?.token ?? this.prescription.directions;
            const directionLines = [];
            if (baseDirections) {
                directionLines.push(`Directions Translated From: ${baseDirections}`);
            }
            if (this.prescription.memo) {
                directionLines.push(this.prescription.memo);
            }
            this.prescription.memo = directionLines.join("\n");
            this.prescription.directions = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.loadingDirections = false;
    }

    @Watch('doctor')
    async fetchDoctorProgram() {
        if (!this.doctor?.programId) {
            this.doctorProgram = new Program();
            return;
        }

        try {
            const response = await Axios.get<Program>("/api/Program/" + this.doctor.programId);
            this.doctorProgram = Object.assign(new Program, response.data);
        } catch {
            this.doctorProgram = new Program();
        }
    }

    autoselectStore(options: Array<Store>) {
        const activeStores = options.filter(o => o.active);
        return (activeStores.length !== 1) ? undefined : activeStores[0];
    }

    async createPAPApplication() {
        const confirmed = await this.$bvModal.msgBoxConfirm("Create a PAP application from this script?", {
            title: 'Create PAP Application',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Create Application',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            centered: true,
        });

        if (!confirmed) return;

        try {
            await this.$http.post(`/PatientAssistanceProgram/CreateFromScript/${this.prescription?.rxID}`);
            this.$notification(NotificationOptions.success("PAP created successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected customContactPrescriberTemplates: PrescriberContactTemplate[] = [];
    private customContactPrescriberTemplate: PrescriberContactTemplate | null = null;

    async getPrescriberCustomContactTemplates() {
        try {
            const resp = await Axios.get("/Prescriber/contact-templates/true");
            this.customContactPrescriberTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    get filteredCustomContactPrescriptionTemplates() {
        return this.customContactPrescriberTemplates.filter(t => {
            if (!t.programs.length) return true;
            return t.programs.find(p => p.id === this.program?.id);
        });
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }

    protected localDispenseItems: PrescriptionDispenseItem[] = [];

    get dispenseItems() {
        if (this.disableFields) return this.prescription.dispensingItems;
        return [...this.localDispenseItems, {}];
    }

    updateDispenseItem(key: string, index: number, value: any) {
        if (this.localDispenseItems.length === index) {
            this.localDispenseItems.push({});
        }

        const item = this.localDispenseItems[index];
        (item as any)[key] = value;

        this.localDispenseItems = this.localDispenseItems
            .filter(di => di.lotNumber || di.serialNumber || di.expirationDate || di.quantity);
    }

    onTransferCompleted(transferData: any) {
        if (transferData?.prescription) {
            this.prescription = Object.assign(new Prescription(), transferData.prescription);
            this.fetchPrescription(this.prescription);
        }

        this.updateAlerts();
    }

    async markAsNotAScript() {
        if (!this.queueItemID) {
            return;
        }

        try {
            // Create queue item with only the ID (no storeID/rxNumber/rfNumber = "Not A Script")
            const queueItem: QueueItem = {
                id: this.queueItemID
            };

            // Mark as complete (not a script)
            await Axios.post<QueueItem[]>(`/IngestionQueue/`, [queueItem]);

            this.$notification({
                message: 'Item marked as "Not A Script"',
                timeout: 3000,
                icon: "now-ui-icons ui-1_check",
                horizontalAlign: "center",
                verticalAlign: "top",
                type: "success"
            });

            if (this.popQueue()) {
                // Successfully popped to next queued item
                return;
            }

            const hasNextItem = await this.handleAutoModeCompletion();

            if (!hasNextItem && this.fromIngestion) {
                this.backToPreviousPage(true);
            }
        } catch (error) {
            console.error('Error marking as not a script:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async handleAutoModeCompletion(): Promise<boolean> {
        return await this.handleAutoModeNextItem();
    }

    // MedSync Integration Methods
    handleViewMedSyncDetails() {
        if (this.medSyncDetailsModal) {
            this.medSyncDetailsModal.show();
        }
    }

    handleAddToMedSync(data: { enrollmentId: number | null; rxId: string | null }) {
        if (this.addToMedSyncModal && data.rxId) {
            this.addToMedSyncModal.show(data.rxId);
        }
    }

    async handleMedSyncAdded(data: { rxId: string; enrollmentId: number }) {
        // Refresh the banner to show updated medication count
        if (this.medSyncBanner) {
            await this.medSyncBanner.refresh();
        }
    }
}
</script>

<style lang="scss">
.office-contacts-popover .card:last-child {
    margin-bottom: 0;
}
</style>

<style scoped lang="scss">
/*noinspection CssUnusedSymbol*/
::v-deep .modal-dialog {
    right: 20px;
    position: absolute;
    min-width: 600px;
}

#prescription-image {
    min-height: 880px;
}

.image-wrapper {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch !important;

    .actions {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(auto-fill, minmax(calc(min(100%, 200px)), 1fr));
        margin-bottom: 10px;

        &:has(button:nth-child(1):last-child) {
            grid-template-columns: 1fr;
        }

        &:has(button:nth-child(2):last-child) {
            grid-template-columns: repeat(2, 1fr);
        }

        button {
            padding-left: 4px;
            padding-right: 4px;
        }
    }
}

.script-image {
    height: 100vh;
    border: 1px solid black;
    flex-grow: 1;
    max-height: 100vh !important;
}

table.dispense-items ::v-deep(th) {
    font-weight: normal !important;
}
</style>
